import { useSearchParams } from 'react-router-dom';
import React, { useEffect, useState, useRef, useCallback, useLayoutEffect } from 'react';
import { useGameStore } from '@/stores/gameStore';
import type { Player, Question } from '@/types';
import { useCurrentSession, useSessionActions } from '@/stores/gameSessionStore';
import { DEFAULT_TIMER_CONFIG } from '@/config/timerConfig';
import useSessionPhaseTimer from '@/hooks/useSessionPhaseTimer';
import { CharacterSpeedManager } from '@/utils/CharacterSpeedManager';
import ItemDebugDisplay from '@/components/ItemDebugDisplay';
import { getShoesItem, getTurboItem } from '@/constants/bonusItems';
import { GRENADE_MAX_CHARGE_MS, getGrenadeChargeAmount } from '@/constants/grenade';
import { ARENA_PLAYER_START_POSITION } from '@/constants/arena';
import { authService } from '@/services/auth';
import { audioManager } from '@/utils/audioManager';
import { PLAYER_SOUNDS } from '@/config/soundEffects';
import { CHARACTERS, getCharacterById } from '@/constants/characters';
import logger from '@/utils/logger';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { useIsTouchDevice } from '@/hooks/useIsTouchDevice';
import VirtualJoystick from '@/components/VirtualJoystick';
import MobileActionButtons from '@/components/MobileActionButtons';
import MobileGameOverlay from '@/components/MobileGameOverlay';
import ArenaQuestionPeekButton from '@/components/ArenaQuestionPeekButton';
import DraggableResizableWindow from '@/components/DraggableResizableWindow';
import { arenaRealtimeService } from '@/services/realtime';
import { nowServer } from '@/services/firebase/rtdb';
import type { PositionUpdate } from '@/types/realtime';
import {
  getQuestionOverlayVisibility,
  type QuestionOverlayVisibilityEvent,
} from './questionOverlayVisibility';

// Interface for individual AI player state
interface AiPlayerState {
  id: string;
  position: { x: number; y: number };
  selectedChoice: number | null;
  isMoving: boolean;
  facingDirection: 'left' | 'right';
  targetAnswer: number | null;
  moveStartTime: number;
  decisionMade: boolean;
  accuracy: number; // 0.0 to 1.0 - how often this AI gets answers right
  responseDelay: number; // milliseconds delay before starting to move
  speedModeId: number;
  baseSpeed: number;
  speedBoostMultiplier: number;
  hasActivatedTurbo: boolean;
  turboExpiresAt: number | null;
  netCooldownUntil: number;
  lastNetAttemptAt: number;
  grenadeCooldownUntil: number;
  lastGrenadeAttemptAt: number;
}

interface RemotePlayerMotionState {
  displayPosition: { x: number; y: number };
  targetPosition: { x: number; y: number };
  velocity: { x: number; y: number };
  lastSnapshotTime: number;
  lastPositionUpdateTime: number;
  lastSourceTimestamp: number | null;
  lastMotionTime: number;
  facingDirection: 'left' | 'right';
}

/** One authoritative sample from another player, ready for bracket-lerp. */
interface RemoteSample {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Server-aligned sender timestamp (ms). Buffer is sorted newest-last. */
  ts: number;
}

/** Per-player ring buffer feeding the entity-interpolation render path. */
interface RemoteBuffer {
  samples: RemoteSample[];
  displayX: number;
  displayY: number;
  displayInitialized: boolean;
  lastMotionTime: number;
  facingDirection: 'left' | 'right';
  /**
   * True once the sender has broadcast an authoritative facing (`pos.dir`).
   * While true, the render loop stops inferring facing from frame-level
   * displayX deltas — inference is unreliable for stationary players because
   * the exponential ease tail can nudge `displayX` backward by noise and
   * latch facing to the wrong side.
   */
  hasAuthoritativeFacing: boolean;
}

interface RemotePlayerRenderState {
  position: { x: number; y: number };
  isMoving: boolean;
  facingDirection: 'left' | 'right';
}

interface RemoteMotionDebugSample {
  capturedAt: string;
  sessionId: string | null;
  playerId: string;
  snapshotAgeMs: number;
  extrapolationMs: number;
  velocityX: number;
  velocityY: number;
  velocityMagnitude: number;
  displayToTargetGap: number;
  isMoving: boolean;
  displayX: number;
  displayY: number;
  targetX: number;
  targetY: number;
  interpolationSpeed: number;
  extrapolationLimitMs: number;
  extrapolationStaleMs: number;
}

const WORLD_WIDTH = 1600;
const WORLD_HEIGHT = 900;
const CHARACTER_SIZE = 80;
const ANSWER_CIRCLE_SIZE = 120;
const ANSWER_LOCK_IN_RADIUS = ANSWER_CIRCLE_SIZE * 0.4;
const ANSWER_HOVER_RADIUS = ANSWER_CIRCLE_SIZE * 0.55;
const ANSWER_RIGHT_MARGIN = 60;
const ANSWER_GAP = 50;
const TARGET_FRAME_RATE = 60;
const MAX_FRAME_STEP_SECONDS = 0.05;
const MAX_WORLD_SCALE = 1;
const DEFAULT_SPEED_MODE_ID = 0;
const HEADER_VERTICAL_BUFFER = 24;
// Starting area box dimensions and position
const STARTING_BOX_X = 40;
const STARTING_BOX_Y = 300;
const STARTING_BOX_WIDTH = 200;
const STARTING_BOX_HEIGHT = 400;

// Character spawning within the starting box
const PLAYER_START_X = ARENA_PLAYER_START_POSITION.x;
const PLAYER_START_Y = ARENA_PLAYER_START_POSITION.y;

const getAnswerStackTop = (answerCount: number) => {
  if (answerCount <= 0) {
    return 0;
  }

  const totalStackHeight =
    (answerCount * ANSWER_CIRCLE_SIZE) +
    (Math.max(0, answerCount - 1) * ANSWER_GAP);

  return Math.max(0, (WORLD_HEIGHT - totalStackHeight) / 2);
};

const getAnswerCirclePosition = (index: number, answerCount: number) => {
  const top = getAnswerStackTop(answerCount) + (index * (ANSWER_CIRCLE_SIZE + ANSWER_GAP));
  const left = WORLD_WIDTH - ANSWER_RIGHT_MARGIN - ANSWER_CIRCLE_SIZE;

  return {
    left,
    top,
    centerX: left + (ANSWER_CIRCLE_SIZE / 2),
    centerY: top + (ANSWER_CIRCLE_SIZE / 2)
  };
};

const SHOES_ITEM = getShoesItem();
const TURBO_ITEM = getTurboItem();
const SHOES_MULTIPLIER = SHOES_ITEM.effect.value || 1.25;
const TURBO_MULTIPLIER = TURBO_ITEM.effect.value || 1.5;
const TURBO_DURATION_MS = (TURBO_ITEM.effect.duration || 2) * 1000;
const TURBO_TRIGGER_WINDOW = { min: 250, max: 1600 }; // ms after movement starts
const NET_EFFECTIVE_RANGE = 480;
const NET_VERTICAL_TOLERANCE = 160;
const NET_COOLDOWN_MS = 2600;
const GRENADE_MIN_RANGE = 220;
const GRENADE_MAX_RANGE = 820;
const GRENADE_COOLDOWN_MS = 4200;
const GRENADE_MIN_CHARGE = 0.35;
const POSITION_SYNC_INTERVAL_RTDB_MS = 33; // ~30 Hz for lower motion latency
const POSITION_SYNC_INTERVAL_FALLBACK_MS = 90; // Keep Firestore load moderate in fallback
const POSITION_SYNC_MIN_DISTANCE = 0.5;
const DEFAULT_REMOTE_INTERPOLATION_SPEED = 18;
const DEFAULT_REMOTE_EXTRAPOLATION_LIMIT_MS = 180;
const DEFAULT_REMOTE_EXTRAPOLATION_STALE_MS = 320;
// ─── Buffered remote interpolation (entity-interp model) ─────────────
// Render remote players this many ms behind the newest sample so every frame
// lands between two real authoritative samples instead of extrapolating past
// the newest one.
const REMOTE_RENDER_DELAY_MS = 100;
// If renderTime is past the newest sample (packet drop / stall), extrapolate
// only for this window before clamping. Prevents visible "runaway" drift.
const REMOTE_BUFFER_EXTRAPOLATION_CAP_MS = 100;
// Ring-buffer capacity. At 30 Hz and a 100 ms render delay we typically need
// 2 samples to bracket; 4 gives headroom for short network re-orderings.
const REMOTE_BUFFER_CAPACITY = 4;
// Long gap means the buffer is stale; clear it and seed fresh to avoid
// lerping across a teleport.
const REMOTE_BUFFER_RESET_GAP_MS = 1000;
// Kill switch — flip to false and rebuild to fall back to the original
// extrapolation-from-newest render path (kept in place for fast rollback).
const USE_BUFFERED_REMOTE_INTERPOLATION = true;
const REMOTE_MOTION_RECORD_INTERVAL_MS = 200;
const MAX_REMOTE_MOTION_DEBUG_SAMPLES = 40000;
const REMOTE_STATIONARY_CONFIRM_MS = 100;
const REMOTE_STATIONARY_DAMPING = 0.45;
const REMOTE_MOVEMENT_THRESHOLD = 0.75;
const REMOTE_IDLE_GRACE_MS = 250;
const SPECTATOR_QUESTION_WINDOW_BASE_WIDTH = 1080;
const SPECTATOR_QUESTION_WINDOW_BASE_HEIGHT = 260;
const SPECTATOR_QUESTION_TITLE_BAR_HEIGHT = 38;

const getInitialSpectatorQuestionWindowRect = () => {
  if (typeof window === 'undefined') {
    return { x: 24, y: 16, width: 1120, height: 280 };
  }

  const width = Math.min(1200, Math.max(420, window.innerWidth - 48));
  const height = Math.min(310, Math.max(190, window.innerHeight * 0.31));

  return {
    x: Math.max((window.innerWidth - width) / 2, 10),
    y: 12,
    width,
    height
  };
};

/**
 * Entity-interpolation core: given a buffer of authoritative samples (sorted
 * by ts, newest-last) and a desired `renderTime` (ms, server-aligned), return
 * the position to draw.
 *
 * Three branches:
 *  1. Bracket lerp between samples[i] and samples[i+1] when renderTime falls
 *     inside the buffer — this is the smooth common case.
 *  2. Brief extrapolation past newest (capped) when renderTime is ahead of
 *     everything we have — covers packet drops without popping.
 *  3. Clamp to oldest when renderTime is behind the whole buffer — happens on
 *     fresh joins before the buffer fills.
 */
function computeRemoteTarget(
  samples: readonly RemoteSample[],
  renderTime: number
): { x: number; y: number } | null {
  if (samples.length === 0) return null;

  for (let i = 0; i < samples.length - 1; i++) {
    const a = samples[i];
    const b = samples[i + 1];
    if (a.ts <= renderTime && renderTime < b.ts) {
      const span = b.ts - a.ts;
      const t = span > 0 ? (renderTime - a.ts) / span : 0;
      return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
    }
  }

  const newest = samples[samples.length - 1];
  const age = renderTime - newest.ts;
  if (age >= 0 && age < REMOTE_BUFFER_EXTRAPOLATION_CAP_MS) {
    const dtSec = age / 1000;
    return { x: newest.x + newest.vx * dtSec, y: newest.y + newest.vy * dtSec };
  }

  if (renderTime < samples[0].ts) {
    return { x: samples[0].x, y: samples[0].y };
  }
  return { x: newest.x, y: newest.y };
}

const clampNumber = (value: number, min: number, max: number, fallback: number): number => {
  if (!Number.isFinite(value)) {
    return fallback;
  }

  return Math.min(max, Math.max(min, value));
};

const GameArena = () => {
  const [searchParams] = useSearchParams();
  const navigate = useSafeNavigate();
  const sessionId = searchParams.get('sessionId');
  const playerId = searchParams.get('playerId');
  const isSpectator = searchParams.get('spectator') === 'true';
  const {
    getCurrentQuestion,
    gameState,
    setGameState,
    selectAnswer,
    clearCurrentPlayerAnswer,
    currentQuestionIndex,
    getTotalQuestions,
    selectedCharacter,
    questions,
    initializeGame,
    getCurrentCharacterSpeed,
    recordPlayerResponse,
    startNewRound,
    roundStartTime,
    aiPlayerCount,
    addMultipleAiPlayers,
    hydrateSessionPlayers,
    updateNetProjectiles,
    updatePlayerImmobilization,
    updatePlayerPosition,
    syncSessionNetProjectiles,
    activeNetProjectiles,
    // throwGrenade, // Accessed via getState() in event handlers
    updateGrenadeProjectiles,
    updatePlayerPushEffects,
    syncSessionGrenadeProjectiles,
    processGrenadeThrowEventsFromSession,
    activeGrenadeProjectiles,
    grenadeChargeStartTime,
    applyRoundBasedItemEffects,
    characterSpeedData,
    setCurrentPlayerId,
    players,
    currentPlayer: storeCurrentPlayer,
    currentPlayerId: storeCurrentPlayerId
  } = useGameStore();
  const gameMode = useGameStore(state => state.gameMode);

  const currentCharacter = getCharacterById(selectedCharacter);
  const resolvedCurrentPlayerId = storeCurrentPlayer?.id || storeCurrentPlayerId || 'current-player';
  const currentPlayer = storeCurrentPlayer || players.find(p => p.id === resolvedCurrentPlayerId);
  const hasShoesItem = Boolean(currentPlayer?.items?.some(item => item.type === 'shoes'));
  const hasActiveShoesModifier = Boolean(
    characterSpeedData?.modifiers?.some(modifier => modifier.source === 'shoes_item')
  );
  const currentSession = useCurrentSession();
  const activeSession = sessionId ? currentSession : null;
  const isAiMode = gameMode === 'ai';
  const aiRunningAdvanceRef = useRef(false);
  const hasInitializedArenaRef = useRef(false);
  const rtdbProbeSessionRef = useRef<string | null>(null);
  const { selectSession, subscribeToSession, unsubscribeFromSession } = useSessionActions();

  // Track RTDB health as state so effects re-run when RTDB becomes unavailable
  const [rtdbAvailable, setRtdbAvailable] = useState(() => arenaRealtimeService.isAvailable());
  useEffect(() => {
    return arenaRealtimeService.onUnhealthy(() => setRtdbAvailable(false));
  }, []);

  useEffect(() => {
    if (!playerId || isSpectator) {
      return;
    }

    setCurrentPlayerId(playerId);
  }, [playerId, isSpectator, setCurrentPlayerId]);

  // In spectator mode, use session players instead of store players
  const displayPlayers = players;

  const hasHydratedFromCacheRef = useRef(false);
  const spectatorUnsubscribeRef = useRef<(() => void) | null>(null);

  // In spectator mode, we need to select and monitor the session
  useEffect(() => {
    if (isSpectator && sessionId) {
      selectSession(sessionId);
      const unsubscribe = subscribeToSession(sessionId);
      spectatorUnsubscribeRef.current = unsubscribe;

      return () => {
        if (spectatorUnsubscribeRef.current) {
          spectatorUnsubscribeRef.current();
          spectatorUnsubscribeRef.current = null;
        }
        unsubscribeFromSession();
      };
    }
  }, [isSpectator, sessionId, selectSession, subscribeToSession, unsubscribeFromSession]);

  const hydrateFromCachedQuestions = useCallback(() => {
    if (hasHydratedFromCacheRef.current) {
      return false;
    }

    if (typeof window === 'undefined') {
      return false;
    }

    if (!sessionId || questions.length > 0) {
      return false;
    }

    try {
      const preloadKey = `arena_preload_${sessionId}`;
      const cachedValue = sessionStorage.getItem(preloadKey);

      if (!cachedValue) {
        return false;
      }

      const parsedValue = JSON.parse(cachedValue) as {
        questions?: Question[];
        currentQuestionIndex?: number;
        gameState?: string;
      } | null;

      const cachedQuestions = Array.isArray(parsedValue?.questions) ? parsedValue.questions : [];

      if (cachedQuestions.length === 0) {
        return false;
      }

      initializeGame(sessionId, cachedQuestions);
      hasHydratedFromCacheRef.current = true;

      const storedIndex = typeof parsedValue?.currentQuestionIndex === 'number'
        ? parsedValue?.currentQuestionIndex
        : null;

      if (storedIndex !== null) {
        const storeState = useGameStore.getState();
        if (storeState.currentQuestionIndex !== storedIndex) {
          storeState.syncQuestionIndex(storedIndex);
        }
      }

      const targetGameState = activeSession?.gameState ?? parsedValue?.gameState;
      if (typeof targetGameState === 'string' && gameState !== targetGameState) {
        setGameState(targetGameState as typeof gameState);
      }

      return true;
    } catch (error) {
      console.error('Failed to hydrate arena from cached questions:', error);
      return false;
    }
  }, [
    sessionId,
    questions.length,
    initializeGame,
    activeSession?.gameState,
    gameState,
    setGameState
  ]);

  useEffect(() => {
    const hydrated = hydrateFromCachedQuestions();
    if (hydrated) {
      return;
    }

    if (typeof window === 'undefined' || !sessionId) {
      return;
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.storageArea !== sessionStorage) {
        return;
      }

      if (event.key && event.key !== `arena_preload_${sessionId}`) {
        return;
      }

      hydrateFromCachedQuestions();
    };

    window.addEventListener('storage', handleStorage);

    // Attempt one more hydration shortly after mount in case storage populates asynchronously
    const retryTimeout = window.setTimeout(() => {
      hydrateFromCachedQuestions();
    }, 150);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.clearTimeout(retryTimeout);

      // Cleanup sessionStorage on unmount to prevent memory leaks
      if (sessionId && typeof window !== 'undefined') {
        try {
          const preloadKey = `arena_preload_${sessionId}`;
          sessionStorage.removeItem(preloadKey);
        } catch {
          // Silently ignore if sessionStorage is unavailable
        }
      }
    };
  }, [hydrateFromCachedQuestions, sessionId]);

  // Track changes in selectedCharacter
  useEffect(() => {
    if (selectedCharacter === null) {
      logger.debug('GameArena mounted without a selected character');
    }
  }, [selectedCharacter]);

  // Player position and movement state
  const [playerPosition, setPlayerPosition] = useState(() => ({ x: PLAYER_START_X, y: PLAYER_START_Y }));
  const playerPositionRef = useRef<{ x: number; y: number }>({ x: PLAYER_START_X, y: PLAYER_START_Y });
  const [selectedChoice, setSelectedChoice] = useState<number | null>(null);
  const [hoveredChoice, setHoveredChoice] = useState<number | null>(null);
  const [consideringChoice, setConsideringChoice] = useState<number | null>(null);
  const [showPlayerArrow, setShowPlayerArrow] = useState(false);
  const [selectionTimer, setSelectionTimer] = useState<number>(0);
  const [isMoving, setIsMoving] = useState(false);
  const [facingDirection, setFacingDirection] = useState<'left' | 'right'>('right');
  const [keysPressed, setKeysPressed] = useState<Set<string>>(new Set());
  const [worldScale, setWorldScale] = useState(1);
  const [worldOffset, setWorldOffset] = useState({ x: 0, y: 0 });
  const headerRef = useRef<HTMLDivElement | null>(null);
  const [spectatorHeaderHeight, setSpectatorHeaderHeight] = useState(0);
  const debugVisible = false;
  const isTouchDevice = useIsTouchDevice();
  const joystickDirectionRef = useRef({ x: 0, y: 0 });

  const handleJoystickChange = useCallback((direction: { x: number; y: number }) => {
    joystickDirectionRef.current = direction;
  }, []);

  // Primary action handler (turbo or net) — shared between keyboard Space and mobile button
  const handlePrimaryAction = useCallback(() => {
    const {
      players: storePlayers,
      throwNet,
      activateTurboItem,
      currentPlayer: stateCurrentPlayer,
      currentPlayerId: stateCurrentPlayerId
    } = useGameStore.getState();

    const resolvedId = stateCurrentPlayer?.id || stateCurrentPlayerId || 'current-player';
    const player = stateCurrentPlayer || storePlayers.find(p => p.id === resolvedId);

    if (player && !player.isImmobilized) {
      // Priority 1: Turbo
      if (player.turboAvailable && !player.turboUsed) {
        const success = activateTurboItem(resolvedId);
        if (success) return;
      }
      // Priority 2: Net
      const netCharges = player.netCharges || 0;
      if (netCharges > 0) {
        const throwDirection = facingDirectionRef.current === 'left' ? { x: -1, y: 0 } : { x: 1, y: 0 };
        throwNet(resolvedId, throwDirection, playerPositionRef.current);
      }
    }
  }, []);

  // Multi-AI Character state
  const [aiPlayers, setAiPlayers] = useState<AiPlayerState[]>([]);
  const aiPlayersRef = useRef<AiPlayerState[]>([]);

  // Remote player movement tracking
  const remotePlayerMotionRef = useRef<Map<string, RemotePlayerMotionState>>(new Map());
  // Parallel per-player ring buffer feeding the entity-interpolation path.
  // Populated alongside `remotePlayerMotionRef` so rollback via
  // USE_BUFFERED_REMOTE_INTERPOLATION costs nothing at runtime.
  const remoteBuffersRef = useRef<Map<string, RemoteBuffer>>(new Map());
  const [remotePlayerRenderStates, setRemotePlayerRenderStates] = useState<Map<string, RemotePlayerRenderState>>(new Map());
  const [showRemoteMotionDebug, setShowRemoteMotionDebug] = useState(() => {
    const debugParam = searchParams.get('motionDebug');
    return debugParam === '1' || debugParam === 'true';
  });
  const [remoteInterpolationSpeed, setRemoteInterpolationSpeed] = useState(DEFAULT_REMOTE_INTERPOLATION_SPEED);
  const [remoteExtrapolationLimitMs, setRemoteExtrapolationLimitMs] = useState(DEFAULT_REMOTE_EXTRAPOLATION_LIMIT_MS);
  const [remoteExtrapolationStaleMs, setRemoteExtrapolationStaleMs] = useState(DEFAULT_REMOTE_EXTRAPOLATION_STALE_MS);
  const [isRemoteMotionRecording, setIsRemoteMotionRecording] = useState(false);
  const [remoteMotionSampleCount, setRemoteMotionSampleCount] = useState(0);
  const [remoteMotionRecordStartedAtMs, setRemoteMotionRecordStartedAtMs] = useState<number | null>(null);
  const [showQuestionOverlay, setShowQuestionOverlay] = useState(false);
  const updateQuestionOverlayVisibility = useCallback((event: QuestionOverlayVisibilityEvent) => {
    setShowQuestionOverlay(isVisible => getQuestionOverlayVisibility(isVisible, event));
  }, []);
  const [spectatorQuestionWindowRect, setSpectatorQuestionWindowRect] = useState(getInitialSpectatorQuestionWindowRect);
  const consideringChoiceRef = useRef<number | null>(null);
  const remotePlayerRenderStatesRef = useRef<Map<string, RemotePlayerRenderState>>(new Map());
  const remoteMotionDebugLogRef = useRef<RemoteMotionDebugSample[]>([]);
  const remoteRoundResetKeyRef = useRef<string | null>(null);

  const keysPressedRef = useRef(keysPressed);
  useEffect(() => {
    keysPressedRef.current = keysPressed;
  }, [keysPressed]);

  useEffect(() => {
    consideringChoiceRef.current = consideringChoice;
  }, [consideringChoice]);

  const animationFrameRef = useRef<number | null>(null);
  const lastFrameTimeRef = useRef<number | null>(null);
  const facingDirectionRef = useRef<'left' | 'right'>(facingDirection);
  const isPlayerImmobilizedRef = useRef<boolean>(!!currentPlayer?.isImmobilized);

  // Position syncing to Firebase, serialized so older writes cannot overtake newer ones.
  const lastSyncedPositionRef = useRef<{ x: number; y: number } | null>(null);
  const lastSyncTimestampRef = useRef<number | null>(null);
  const lastSyncedDirRef = useRef<'left' | 'right' | null>(null);
  const isPositionSyncInFlightRef = useRef(false);
  const hasPendingPositionSyncRef = useRef(false);

  useEffect(() => {
    if (!sessionId || !playerId || isSpectator || isAiMode) {
      lastSyncedPositionRef.current = null;
      lastSyncTimestampRef.current = null;
      lastSyncedDirRef.current = null;
      isPositionSyncInFlightRef.current = false;
      hasPendingPositionSyncRef.current = false;
      return;
    }

    let isCancelled = false;

    const syncLatestPosition = async () => {
      if (isCancelled) {
        return;
      }

      if (isPositionSyncInFlightRef.current) {
        hasPendingPositionSyncRef.current = true;
        return;
      }

      const latestPosition = playerPositionRef.current;
      const latestDir = facingDirectionRef.current;
      const lastSyncedPosition = lastSyncedPositionRef.current;
      const lastSyncedDir = lastSyncedDirRef.current;

      // Skip the write only when BOTH position and facing are effectively unchanged.
      // Facing can change without movement (e.g., brief arrow-key tap, push aftermath,
      // immobilised-then-turned) and those facing-only changes must still propagate —
      // otherwise remote clients have no way to learn the sender's facing once the
      // player is stationary.
      const positionQuiet =
        lastSyncedPosition !== null &&
        Math.hypot(
          latestPosition.x - lastSyncedPosition.x,
          latestPosition.y - lastSyncedPosition.y
        ) < POSITION_SYNC_MIN_DISTANCE;
      const facingQuiet = lastSyncedDir === latestDir;
      if (positionQuiet && facingQuiet) {
        return;
      }

      hasPendingPositionSyncRef.current = false;
      isPositionSyncInFlightRef.current = true;

      // Authoritative velocity from the sender: bypasses receiver-side clock
      // skew that would otherwise distort inferred velocity. Dt is measured
      // on the sender's clock between consecutive writes.
      const syncNow = nowServer();
      const prevPosition = lastSyncedPosition;
      const prevSyncTs = lastSyncTimestampRef.current;
      let vx: number | undefined;
      let vy: number | undefined;
      if (prevPosition && prevSyncTs !== null) {
        const dtSec = (syncNow - prevSyncTs) / 1000;
        if (dtSec > 0) {
          vx = (latestPosition.x - prevPosition.x) / dtSec;
          vy = (latestPosition.y - prevPosition.y) / dtSec;
        } else {
          vx = 0;
          vy = 0;
        }
      }

      try {
        if (arenaRealtimeService.isAvailable()) {
          arenaRealtimeService.writePosition(sessionId, playerId, {
            x: latestPosition.x,
            y: latestPosition.y,
            vx,
            vy,
            dir: latestDir,
          });
        } else {
          await authService.updatePlayerPositionDirect(sessionId, playerId, latestPosition);
        }

        if (!isCancelled) {
          lastSyncedPositionRef.current = { ...latestPosition };
          lastSyncTimestampRef.current = syncNow;
          lastSyncedDirRef.current = latestDir;
        }
      } catch {
        // Position updates are best-effort during arena play.
      } finally {
        if (!isCancelled) {
          isPositionSyncInFlightRef.current = false;
          if (hasPendingPositionSyncRef.current) {
            hasPendingPositionSyncRef.current = false;
            void syncLatestPosition();
          }
        }
      }
    };

    const syncIntervalMs = rtdbAvailable ? POSITION_SYNC_INTERVAL_RTDB_MS : POSITION_SYNC_INTERVAL_FALLBACK_MS;

    const intervalId = window.setInterval(() => {
      void syncLatestPosition();
    }, syncIntervalMs);

    void syncLatestPosition();

    return () => {
      isCancelled = true;
      window.clearInterval(intervalId);
      isPositionSyncInFlightRef.current = false;
      hasPendingPositionSyncRef.current = false;
    };
  }, [sessionId, playerId, isSpectator, isAiMode, rtdbAvailable]);

  // Probe RTDB connectivity when entering the arena for this session.
  // Fallback is triggered only by probe/write failures, not by idle/no-motion periods.
  useEffect(() => {
    if (!sessionId || isAiMode || !rtdbAvailable) {
      if (!sessionId) {
        rtdbProbeSessionRef.current = null;
      }
      return;
    }

    if (rtdbProbeSessionRef.current === sessionId) {
      return;
    }

    rtdbProbeSessionRef.current = sessionId;
    let cancelled = false;

    arenaRealtimeService.verifyConnection(sessionId).then((healthy) => {
      if (cancelled || healthy) {
        return;
      }

      arenaRealtimeService.forceFallback(`arena entry probe failed for session ${sessionId}`);
    });

    return () => {
      cancelled = true;
    };
  }, [sessionId, isAiMode, rtdbAvailable]);

  // RTDB inbound position listener — feeds remote player positions into the interpolation buffer.
  useEffect(() => {
    if (!sessionId || isAiMode || !rtdbAvailable) return;

    /**
     * Append an authoritative sample to the entity-interpolation ring buffer
     * for this player. Runs in parallel with the legacy motion-state update
     * below so the old render path stays available behind the rollback flag.
     */
    const ingestRemoteSample = (remotePlayerId: string, raw: PositionUpdate) => {
      const sourceTimestamp = Number.isFinite(raw.ts) ? raw.ts : null;
      if (sourceTimestamp === null) return false;

      const buffers = remoteBuffersRef.current;
      const existingBuf = buffers.get(remotePlayerId);
      const buf: RemoteBuffer = existingBuf ?? {
        samples: [],
        displayX: raw.x,
        displayY: raw.y,
        displayInitialized: false,
        lastMotionTime: performance.now(),
        facingDirection: raw.dir ?? 'right',
        hasAuthoritativeFacing: raw.dir === 'left' || raw.dir === 'right',
      };

      // Authoritative facing from the sender always wins over inferred facing.
      if (raw.dir === 'left' || raw.dir === 'right') {
        buf.facingDirection = raw.dir;
        buf.hasAuthoritativeFacing = true;
      }

      const newest = buf.samples[buf.samples.length - 1];
      // Drop out-of-order packets.
      if (newest && sourceTimestamp <= newest.ts) return false;
      // Long gap: clear buffer to avoid lerping across a teleport.
      let resetDisplayToSample = false;
      if (newest && sourceTimestamp - newest.ts > REMOTE_BUFFER_RESET_GAP_MS) {
        buf.samples.length = 0;
        buf.displayX = raw.x;
        buf.displayY = raw.y;
        buf.displayInitialized = true;
        buf.lastMotionTime = performance.now();
        resetDisplayToSample = true;
      }

      // Prefer packet-provided velocity; otherwise infer from the prior sample.
      let vx = typeof raw.vx === 'number' && Number.isFinite(raw.vx) ? raw.vx : 0;
      let vy = typeof raw.vy === 'number' && Number.isFinite(raw.vy) ? raw.vy : 0;
      if ((raw.vx === undefined || raw.vy === undefined) && newest && !resetDisplayToSample) {
        const dtSec = (sourceTimestamp - newest.ts) / 1000;
        if (dtSec > 0) {
          if (raw.vx === undefined) vx = (raw.x - newest.x) / dtSec;
          if (raw.vy === undefined) vy = (raw.y - newest.y) / dtSec;
        }
      }

      buf.samples.push({ x: raw.x, y: raw.y, vx, vy, ts: sourceTimestamp });
      while (buf.samples.length > REMOTE_BUFFER_CAPACITY) {
        buf.samples.shift();
      }
      buffers.set(remotePlayerId, buf);
      return resetDisplayToSample;
    };

    const unsub = arenaRealtimeService.onPositionChanges(sessionId, (remotePlayerId, pos) => {
      // Always skip AI players and the default local player placeholder
      if (remotePlayerId === 'current-player' || remotePlayerId.startsWith('ai-player-')) {
        return;
      }
      // Skip own player updates (prevents feedback loop) — but NOT when spectating,
      // because a spectator has no "self" and must process ALL real players.
      if (!isSpectator && (remotePlayerId === resolvedCurrentPlayerId || remotePlayerId === playerId)) {
        return;
      }

      const resetDisplayToSample = ingestRemoteSample(remotePlayerId, pos);

      const now = performance.now();
      const motionStates = remotePlayerMotionRef.current;
      const existing = motionStates.get(remotePlayerId);

      if (!existing) {
        const sourceTimestamp = Number.isFinite(pos.ts) ? pos.ts : null;
        const initialFacing = pos.dir ?? 'right';
        motionStates.set(remotePlayerId, {
          displayPosition: { x: pos.x, y: pos.y },
          targetPosition: { x: pos.x, y: pos.y },
          velocity: { x: 0, y: 0 },
          lastSnapshotTime: now,
          lastPositionUpdateTime: now,
          lastSourceTimestamp: sourceTimestamp,
          lastMotionTime: now,
          facingDirection: initialFacing,
        });

        // Trigger a render state update for the new player
        setRemotePlayerRenderStates(prev => {
          const next = new Map(prev);
          next.set(remotePlayerId, {
            position: { x: pos.x, y: pos.y },
            isMoving: false,
            facingDirection: initialFacing,
          });
          return next;
        });
        return;
      }

      const sourceTimestamp = Number.isFinite(pos.ts) ? pos.ts : null;
      if (
        sourceTimestamp !== null &&
        existing.lastSourceTimestamp !== null &&
        sourceTimestamp <= existing.lastSourceTimestamp
      ) {
        return;
      }

      if (resetDisplayToSample) {
        existing.displayPosition = { x: pos.x, y: pos.y };
        existing.targetPosition = { x: pos.x, y: pos.y };
        existing.velocity = { x: 0, y: 0 };
        existing.lastSnapshotTime = now;
        existing.lastPositionUpdateTime = now;
        existing.lastSourceTimestamp = sourceTimestamp;
        existing.lastMotionTime = now;
        if (pos.dir === 'left' || pos.dir === 'right') {
          existing.facingDirection = pos.dir;
        }

        setRemotePlayerRenderStates(prev => {
          const next = new Map(prev);
          next.set(remotePlayerId, {
            position: { x: pos.x, y: pos.y },
            isMoving: false,
            facingDirection: existing.facingDirection,
          });
          return next;
        });
        return;
      }

      const deltaX = pos.x - existing.targetPosition.x;
      const deltaY = pos.y - existing.targetPosition.y;

      if (deltaX === 0 && deltaY === 0) {
        existing.velocity = { x: 0, y: 0 };
        existing.targetPosition = { x: pos.x, y: pos.y };
        existing.lastSnapshotTime = now;
        existing.lastPositionUpdateTime = now;
        existing.lastSourceTimestamp = sourceTimestamp;
        // Facing-only updates (sender turned without moving) must still land.
        if (pos.dir === 'left' || pos.dir === 'right') {
          existing.facingDirection = pos.dir;
        }
        return;
      }

      const sourceDeltaMs =
        sourceTimestamp !== null && existing.lastSourceTimestamp !== null
          ? sourceTimestamp - existing.lastSourceTimestamp
          : null;
      const arrivalDeltaMs = now - existing.lastPositionUpdateTime;
      const baseDeltaMs = sourceDeltaMs !== null && sourceDeltaMs > 0 && sourceDeltaMs < 1000
        ? sourceDeltaMs
        : arrivalDeltaMs;
      const velocityDeltaMs = Math.max(baseDeltaMs, 16);

      existing.velocity = {
        x: (deltaX / velocityDeltaMs) * 1000,
        y: (deltaY / velocityDeltaMs) * 1000,
      };
      existing.targetPosition = { x: pos.x, y: pos.y };
      existing.lastSnapshotTime = now;
      existing.lastPositionUpdateTime = now;
      existing.lastSourceTimestamp = sourceTimestamp;

      if (pos.dir === 'left' || pos.dir === 'right') {
        existing.facingDirection = pos.dir;
      } else if (Math.abs(deltaX) > 0.5) {
        existing.facingDirection = deltaX > 0 ? 'right' : 'left';
      }
    });

    return () => {
      unsub();
    };
  }, [sessionId, isAiMode, isSpectator, resolvedCurrentPlayerId, playerId, rtdbAvailable]);

  // RTDB combat event listener — processes grenade/net throws from other players
  useEffect(() => {
    if (!sessionId || isAiMode || !rtdbAvailable) return;

    const { handleInboundCombatEvent } = useGameStore.getState();

    const unsub = arenaRealtimeService.onCombatEvent(sessionId, (event) => {
      handleInboundCombatEvent(event);
    });

    return () => {
      unsub();
    };
  }, [sessionId, isAiMode, rtdbAvailable]);

  // RTDB player state listener — syncs charges, immobilization, turbo from other players
  useEffect(() => {
    if (!sessionId || isAiMode || !rtdbAvailable) return;

    const unsub = arenaRealtimeService.onPlayerStateChanges(sessionId, (remotePlayerId, state) => {
      const isOwnPlayerState =
        !isSpectator && (remotePlayerId === resolvedCurrentPlayerId || remotePlayerId === playerId);

      // Merge into local player state
      useGameStore.setState(prev => {
        const now = Date.now();
        let changed = false;

        const mergeArenaState = (player: Player): Player => {
          if (player.id !== remotePlayerId) {
            return player;
          }

          let nextPlayer = player;
          const applyPatch = (patch: Partial<Player>) => {
            nextPlayer = { ...nextPlayer, ...patch };
            changed = true;
          };

          // The local client owns its charges/turbo state. Only accept those
          // fields for remote players/spectators, otherwise a delayed RTDB
          // echo can roll local UI state backwards.
          if (!isOwnPlayerState) {
            const itemPatch: Partial<Player> = {};
            if (typeof state.grenadeCharges === 'number' && player.grenadeCharges !== state.grenadeCharges) {
              itemPatch.grenadeCharges = state.grenadeCharges;
            }
            if (typeof state.netCharges === 'number' && player.netCharges !== state.netCharges) {
              itemPatch.netCharges = state.netCharges;
            }
            if (typeof state.turboAvailable === 'boolean' && player.turboAvailable !== state.turboAvailable) {
              itemPatch.turboAvailable = state.turboAvailable;
            }
            if (typeof state.turboUsed === 'boolean' && player.turboUsed !== state.turboUsed) {
              itemPatch.turboUsed = state.turboUsed;
            }
            if (Object.keys(itemPatch).length > 0) {
              applyPatch(itemPatch);
            }
          }

          if (state.isImmobilized === true) {
            const incomingUntil = typeof state.immobilizedUntil === 'number'
              ? state.immobilizedUntil
              : nextPlayer.immobilizedUntil;
            const localUntil = nextPlayer.immobilizedUntil ?? 0;

            if (incomingUntil && incomingUntil > now && (!nextPlayer.isImmobilized || incomingUntil > localUntil)) {
              applyPatch({
                isImmobilized: true,
                immobilizedUntil: incomingUntil
              });
            }
          } else if (!isOwnPlayerState && state.isImmobilized === false) {
            const localUntil = nextPlayer.immobilizedUntil ?? 0;

            // Ignore stale false writes while a locally-known net duration is
            // still active. The local timer clears it when the hold expires.
            if (nextPlayer.isImmobilized && localUntil <= now) {
              applyPatch({
                isImmobilized: false,
                immobilizedUntil: undefined
              });
            }
          } else if (
            state.isImmobilized !== false &&
            typeof state.immobilizedUntil === 'number' &&
            state.immobilizedUntil > (nextPlayer.immobilizedUntil ?? 0)
          ) {
            applyPatch({
              isImmobilized: true,
              immobilizedUntil: state.immobilizedUntil
            });
          }

          return nextPlayer;
        };

        const updatedPlayers = prev.players.map(p => {
          return mergeArenaState(p);
        });

        const updatedCurrentPlayer = prev.currentPlayer?.id === remotePlayerId
          ? mergeArenaState(prev.currentPlayer)
          : prev.currentPlayer;

        return changed ? { players: updatedPlayers, currentPlayer: updatedCurrentPlayer } : prev;
      });
    });

    return () => {
      unsub();
    };
  }, [sessionId, isAiMode, isSpectator, resolvedCurrentPlayerId, playerId, rtdbAvailable]);

  // Sync all session state (players, nets, grenades) from Firebase in one effect
  useEffect(() => {
    if (!activeSession) return;

    const sessionPlayers = activeSession.players;
    if (sessionPlayers && sessionPlayers.length > 0) {
      // Always process session grenade throw metadata as a compatibility path.
      // Deduplication in the store prevents duplicates when RTDB combat events also arrive.
      processGrenadeThrowEventsFromSession(sessionPlayers);
      hydrateSessionPlayers(
        sessionPlayers,
        isSpectator ? null : resolvedCurrentPlayerId
      );
    }

    // Projectile compatibility feed from Firestore.
    // This keeps spectators resilient when RTDB combat events are delayed/missed.
    // Use Array.isArray so empty arrays (server cleared the list on round start)
    // propagate into the store and clear stale projectiles; syncSession*Projectiles
    // already handles the empty case correctly.
    const sessionNets = activeSession.activeNetProjectiles;
    if (Array.isArray(sessionNets)) {
      syncSessionNetProjectiles(sessionNets);
    }

    const sessionGrenades = activeSession.activeGrenadeProjectiles;
    if (Array.isArray(sessionGrenades)) {
      syncSessionGrenadeProjectiles(sessionGrenades);
    }
    // Intentionally narrow: depend on the specific session arrays we hydrate from,
    // not the whole `activeSession` (which changes reference on every snapshot and
    // would re-fire this hydration on unrelated fields like gameState/score).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeSession?.players,
    activeSession?.activeNetProjectiles,
    activeSession?.activeGrenadeProjectiles,
    processGrenadeThrowEventsFromSession,
    hydrateSessionPlayers,
    syncSessionNetProjectiles,
    syncSessionGrenadeProjectiles,
    isSpectator,
    resolvedCurrentPlayerId,
    rtdbAvailable
  ]);

  useEffect(() => {
    playerPositionRef.current = playerPosition;
  }, [playerPosition]);

  useEffect(() => {
    aiPlayersRef.current = aiPlayers;
  }, [aiPlayers]);

  useEffect(() => {
    remotePlayerRenderStatesRef.current = remotePlayerRenderStates;
  }, [remotePlayerRenderStates]);

  useEffect(() => {
    facingDirectionRef.current = facingDirection;
  }, [facingDirection]);

  useEffect(() => {
    if (!activeSession || activeSession.gameState !== 'running') {
      remoteRoundResetKeyRef.current = null;
      return;
    }

    const sourcePlayers = activeSession.players ?? [];
    if (sourcePlayers.length === 0) {
      return;
    }

    const phaseStartMs = activeSession.currentPhaseStartTime?.getTime() ?? 0;
    const roundKey = [
      activeSession.id ?? sessionId ?? 'session',
      activeSession.currentStageIndex,
      activeSession.currentQuestionIndex,
      phaseStartMs,
    ].join(':');

    if (remoteRoundResetKeyRef.current === roundKey) {
      return;
    }

    remoteRoundResetKeyRef.current = roundKey;

    const now = performance.now();
    const motionStates = remotePlayerMotionRef.current;
    const buffers = remoteBuffersRef.current;
    const nextRenderStates = new Map<string, RemotePlayerRenderState>();

    motionStates.clear();
    buffers.clear();

    for (const player of sourcePlayers) {
      if (
        player.id.startsWith('ai-player-') ||
        player.id === 'current-player' ||
        (!isSpectator && (player.id === resolvedCurrentPlayerId || player.id === playerId))
      ) {
        continue;
      }

      const position = player.position ?? ARENA_PLAYER_START_POSITION;
      motionStates.set(player.id, {
        displayPosition: { ...position },
        targetPosition: { ...position },
        velocity: { x: 0, y: 0 },
        lastSnapshotTime: now,
        lastPositionUpdateTime: now,
        lastSourceTimestamp: null,
        lastMotionTime: now,
        facingDirection: 'right'
      });
      buffers.set(player.id, {
        samples: [],
        displayX: position.x,
        displayY: position.y,
        displayInitialized: false,
        lastMotionTime: now,
        facingDirection: 'right',
        hasAuthoritativeFacing: false,
      });
      nextRenderStates.set(player.id, {
        position: { ...position },
        isMoving: false,
        facingDirection: 'right'
      });
    }

    setRemotePlayerRenderStates(nextRenderStates);
  }, [
    activeSession?.id,
    activeSession?.gameState,
    activeSession?.currentStageIndex,
    activeSession?.currentQuestionIndex,
    activeSession?.currentPhaseStartTime,
    activeSession?.players,
    isSpectator,
    playerId,
    resolvedCurrentPlayerId,
    sessionId
  ]);

  useEffect(() => {
    const now = performance.now();
    const nextRemoteIds = new Set<string>();
    const motionStates = remotePlayerMotionRef.current;
    const buffers = remoteBuffersRef.current;
    const remoteSourcePlayers = activeSession?.players ?? [];
    let hasStateShapeChange = false;

    for (const player of remoteSourcePlayers) {
      if (
        player.id.startsWith('ai-player-') ||
        player.id === 'current-player' ||
        (!isSpectator && (player.id === resolvedCurrentPlayerId || player.id === playerId))
      ) {
        continue;
      }

      nextRemoteIds.add(player.id);

      const existingMotion = motionStates.get(player.id);
      if (!existingMotion) {
        motionStates.set(player.id, {
          displayPosition: { ...player.position },
          targetPosition: { ...player.position },
          velocity: { x: 0, y: 0 },
          lastSnapshotTime: now,
          lastPositionUpdateTime: now,
          lastSourceTimestamp: null,
          lastMotionTime: now,
          facingDirection: 'right'
        });
        hasStateShapeChange = true;
        // Create an empty interpolation buffer for this player but do NOT seed
        // a synthetic sample: a seed stamped with nowServer() but carrying a
        // Firestore position makes the bracket-lerp slide visibly from the
        // stale seed to the first real RTDB packet. computeRemoteTarget
        // returns null on an empty buffer, so the render tick falls back to
        // motion.displayPosition until a real sample arrives.
        if (!buffers.has(player.id)) {
          buffers.set(player.id, {
            samples: [],
            displayX: player.position.x,
            displayY: player.position.y,
            displayInitialized: false,
            lastMotionTime: now,
            facingDirection: 'right',
            // Firestore-only fallback path has no `dir` field — stay inferred.
            hasAuthoritativeFacing: false,
          });
        }
        continue;
      }

      // Compatibility feed:
      // 1) Always use session positions when RTDB is unavailable.
      // 2) When RTDB is available, use session positions only until the first
      //    RTDB sample arrives. Firestore positions are reset to the round
      //    start and are not a useful mid-round stale-packet fallback.
      const existingBuffer = buffers.get(player.id);
      const hasRtdbPositionSample =
        existingMotion.lastSourceTimestamp !== null ||
        (existingBuffer?.samples.length ?? 0) > 0;
      const shouldUseSessionPosition =
        !rtdbAvailable ||
        !hasRtdbPositionSample;

      if (!shouldUseSessionPosition) {
        continue;
      }

      const deltaX = player.position.x - existingMotion.targetPosition.x;
      const deltaY = player.position.y - existingMotion.targetPosition.y;

      if (deltaX === 0 && deltaY === 0) {
        const stationaryElapsedMs = now - existingMotion.lastPositionUpdateTime;
        if (stationaryElapsedMs >= REMOTE_STATIONARY_CONFIRM_MS) {
          existingMotion.velocity = { x: 0, y: 0 };
        } else {
          const dampedVelocity = {
            x: existingMotion.velocity.x * REMOTE_STATIONARY_DAMPING,
            y: existingMotion.velocity.y * REMOTE_STATIONARY_DAMPING
          };
          existingMotion.velocity = {
            x: Math.abs(dampedVelocity.x) < 1 ? 0 : dampedVelocity.x,
            y: Math.abs(dampedVelocity.y) < 1 ? 0 : dampedVelocity.y
          };
        }
        existingMotion.lastSnapshotTime = now;
        existingMotion.lastPositionUpdateTime = now;
        existingMotion.lastSourceTimestamp = null;
        continue;
      }

      const deltaMs = Math.max(now - existingMotion.lastPositionUpdateTime, 1);
      existingMotion.velocity = {
        x: (deltaX / deltaMs) * 1000,
        y: (deltaY / deltaMs) * 1000
      };
      existingMotion.targetPosition = { ...player.position };
      existingMotion.lastSnapshotTime = now;
      existingMotion.lastPositionUpdateTime = now;
      existingMotion.lastSourceTimestamp = null;

      if (Math.abs(deltaX) > 0.5) {
        existingMotion.facingDirection = deltaX > 0 ? 'right' : 'left';
      }

      // Push Firestore positions into the interpolation buffer ONLY when RTDB
      // is unhealthy. When RTDB is live, its samples are the authoritative
      // stream; mixing in a Firestore sample stamped with nowServer() but
      // carrying a spatially-stale position pulls the bracket-lerp backward.
      if (!rtdbAvailable) {
        const buf = buffers.get(player.id);
        if (buf) {
          const ts = nowServer();
          const newestBufSample = buf.samples[buf.samples.length - 1];
          if (!newestBufSample || ts > newestBufSample.ts) {
            const dtSec = newestBufSample ? (ts - newestBufSample.ts) / 1000 : 0;
            const vx = dtSec > 0 ? (player.position.x - newestBufSample!.x) / dtSec : 0;
            const vy = dtSec > 0 ? (player.position.y - newestBufSample!.y) / dtSec : 0;
            buf.samples.push({ x: player.position.x, y: player.position.y, vx, vy, ts });
            while (buf.samples.length > REMOTE_BUFFER_CAPACITY) buf.samples.shift();
          }
        }
      }
    }

    for (const existingId of Array.from(motionStates.keys())) {
      if (!nextRemoteIds.has(existingId)) {
        motionStates.delete(existingId);
        buffers.delete(existingId);
        hasStateShapeChange = true;
      }
    }

    if (hasStateShapeChange) {
      setRemotePlayerRenderStates(previous => {
        const next = new Map(previous);

        for (const [playerId, motion] of motionStates) {
          if (!next.has(playerId)) {
            next.set(playerId, {
              position: { ...motion.displayPosition },
              isMoving: false,
              facingDirection: motion.facingDirection
            });
          }
        }

        for (const playerId of Array.from(next.keys())) {
          if (!nextRemoteIds.has(playerId)) {
            next.delete(playerId);
          }
        }

        return next;
      });
    }
  }, [activeSession?.players, isSpectator, playerId, resolvedCurrentPlayerId, rtdbAvailable]);

  useEffect(() => {
    let frameId: number | null = null;
    let lastFrameTime = performance.now();
    let isCancelled = false;
    const tick = (now: number) => {
      if (isCancelled) {
        return;
      }

      // Skip work when no remote players are tracked, but keep the loop alive
      // so it immediately picks up new entries from the RTDB listener.
      if (remotePlayerMotionRef.current.size === 0) {
        frameId = window.requestAnimationFrame(tick);
        return;
      }

      const deltaSeconds = Math.min((now - lastFrameTime) / 1000, MAX_FRAME_STEP_SECONDS);
      lastFrameTime = now;

      const nextRenderStates = new Map<string, RemotePlayerRenderState>();

      if (USE_BUFFERED_REMOTE_INTERPOLATION) {
        // ─── Buffered entity-interpolation render path ───────────────
        // Lerp between two real samples landing around `renderTime` instead
        // of extrapolating past the newest one. Kept gated so a bad sample
        // distribution is a one-constant rollback.
        const renderTime = nowServer() - REMOTE_RENDER_DELAY_MS;
        const buffers = remoteBuffersRef.current;

        for (const [playerId, buf] of buffers.entries()) {
          const target = computeRemoteTarget(buf.samples, renderTime);
          if (!target) {
            // No samples yet — keep whatever display the motion state has so
            // the first render does not snap to (0, 0).
            const motion = remotePlayerMotionRef.current.get(playerId);
            if (motion) {
              nextRenderStates.set(playerId, {
                position: motion.displayPosition,
                isMoving: false,
                facingDirection: buf.facingDirection,
              });
            }
            continue;
          }

          if (!buf.displayInitialized) {
            // Bootstrap from legacy motion.displayPosition (fed by the
            // Firestore compat path) so the ease loop starts from the
            // on-screen location, not the first RTDB target. Without this,
            // the first real packet would cause a visible snap at round
            // boundaries when the buffer was previously empty.
            const motion = remotePlayerMotionRef.current.get(playerId);
            if (motion) {
              buf.displayX = motion.displayPosition.x;
              buf.displayY = motion.displayPosition.y;
            } else {
              buf.displayX = target.x;
              buf.displayY = target.y;
            }
            buf.displayInitialized = true;
          } else {
            // Exponential easing on top of the bracket-lerp removes high-
            // frequency jitter from sub-frame sample quantization.
            const gap = Math.hypot(target.x - buf.displayX, target.y - buf.displayY);
            const adaptiveSpeed = remoteInterpolationSpeed + Math.min(14, gap / 4);
            const alpha = deltaSeconds > 0
              ? 1 - Math.exp(-adaptiveSpeed * deltaSeconds)
              : 0;
            buf.displayX += (target.x - buf.displayX) * alpha;
            buf.displayY += (target.y - buf.displayY) * alpha;
          }

          const movementDistance = Math.hypot(
            target.x - buf.displayX,
            target.y - buf.displayY
          );
          const motion = remotePlayerMotionRef.current.get(playerId);
          const prevDisplayX = motion ? motion.displayPosition.x : buf.displayX;
          const frameDeltaX = buf.displayX - prevDisplayX;

          // When the sender has broadcast authoritative facing (`pos.dir`), trust it —
          // inferring from the interpolated display is unreliable because the ease
          // tail can drift backward by noise and latch facing to the wrong side
          // after the player stops.
          if (!buf.hasAuthoritativeFacing && Math.abs(frameDeltaX) > 0.5) {
            buf.facingDirection = frameDeltaX > 0 ? 'right' : 'left';
          }
          if (movementDistance > REMOTE_MOVEMENT_THRESHOLD) {
            buf.lastMotionTime = now;
          }

          // Keep the legacy motion state's display position mirrored so any
          // code still reading motion.displayPosition (debug panel, etc.)
          // continues to see the current render position.
          if (motion) {
            motion.displayPosition = { x: buf.displayX, y: buf.displayY };
            motion.facingDirection = buf.facingDirection;
          }

          nextRenderStates.set(playerId, {
            position: { x: buf.displayX, y: buf.displayY },
            isMoving:
              movementDistance > REMOTE_MOVEMENT_THRESHOLD ||
              (now - buf.lastMotionTime) < REMOTE_IDLE_GRACE_MS,
            facingDirection: buf.facingDirection,
          });
        }
      } else {
        // ─── Legacy extrapolation-from-newest path (rollback-only) ───
        for (const [playerId, motion] of remotePlayerMotionRef.current.entries()) {
          const snapshotAgeMs = now - motion.lastSnapshotTime;
          if (snapshotAgeMs >= remoteExtrapolationStaleMs) {
            motion.velocity = { x: 0, y: 0 };
          }
          const extrapolationMs = snapshotAgeMs >= remoteExtrapolationStaleMs
            ? 0
            : Math.min(snapshotAgeMs, remoteExtrapolationLimitMs);
          const predictedTargetX = motion.targetPosition.x + ((motion.velocity.x * extrapolationMs) / 1000);
          const predictedTargetY = motion.targetPosition.y + ((motion.velocity.y * extrapolationMs) / 1000);
          const predictionGap = Math.hypot(
            predictedTargetX - motion.displayPosition.x,
            predictedTargetY - motion.displayPosition.y
          );
          const adaptiveInterpolationSpeed = remoteInterpolationSpeed + Math.min(14, predictionGap / 4);
          const adaptiveSmoothingFactor = deltaSeconds > 0
            ? 1 - Math.exp(-adaptiveInterpolationSpeed * deltaSeconds)
            : 0;

          const nextX = motion.displayPosition.x + ((predictedTargetX - motion.displayPosition.x) * adaptiveSmoothingFactor);
          const nextY = motion.displayPosition.y + ((predictedTargetY - motion.displayPosition.y) * adaptiveSmoothingFactor);
          const deltaX = nextX - motion.displayPosition.x;
          const deltaY = nextY - motion.displayPosition.y;
          const movementDistance = Math.hypot(deltaX, deltaY);

          if (Math.abs(deltaX) > 0.5) {
            motion.facingDirection = deltaX > 0 ? 'right' : 'left';
          }

          if (movementDistance > REMOTE_MOVEMENT_THRESHOLD) {
            motion.lastMotionTime = now;
          }

          motion.displayPosition = { x: nextX, y: nextY };

          nextRenderStates.set(playerId, {
            position: motion.displayPosition,
            isMoving: movementDistance > REMOTE_MOVEMENT_THRESHOLD || (now - motion.lastMotionTime) < REMOTE_IDLE_GRACE_MS,
            facingDirection: motion.facingDirection
          });
        }
      }

      setRemotePlayerRenderStates(nextRenderStates);

      // Mirror the interpolated display position into the store so physics
      // (net/grenade collision) checks against what the user actually sees,
      // not the ~100ms-ahead raw RTDB sample. Without this, the net sprite
      // visually overlaps the opponent sprite while the collision check
      // reads a position already past the collision radius. Batched in a
      // single setState to keep per-frame work cheap.
      if (nextRenderStates.size > 0) {
        useGameStore.setState(state => {
          let changed = false;
          const nextPlayers = state.players.map(player => {
            const render = nextRenderStates.get(player.id);
            if (!render) return player;
            if (
              player.position.x === render.position.x &&
              player.position.y === render.position.y
            ) {
              return player;
            }
            changed = true;
            return { ...player, position: { x: render.position.x, y: render.position.y } };
          });
          return changed ? { players: nextPlayers } : {};
        });
      }

      frameId = window.requestAnimationFrame(tick);
    };

    frameId = window.requestAnimationFrame(tick);

    return () => {
      isCancelled = true;
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [
    displayPlayers.length,
    isSpectator,
    resolvedCurrentPlayerId,
    remoteInterpolationSpeed,
    remoteExtrapolationLimitMs,
    remoteExtrapolationStaleMs
  ]);

  useEffect(() => {
    if (!isRemoteMotionRecording) {
      return;
    }

    const appendSamples = () => {
      const now = performance.now();
      const renderStates = remotePlayerRenderStatesRef.current;
      const samplesToAppend: RemoteMotionDebugSample[] = [];

      for (const [id, motion] of remotePlayerMotionRef.current.entries()) {
        const snapshotAgeMs = Math.max(0, now - motion.lastSnapshotTime);
        const extrapolationMs = snapshotAgeMs >= remoteExtrapolationStaleMs
          ? 0
          : Math.min(snapshotAgeMs, remoteExtrapolationLimitMs);
        const velocityMagnitude = Math.hypot(motion.velocity.x, motion.velocity.y);
        const displayToTargetGap = Math.hypot(
          motion.targetPosition.x - motion.displayPosition.x,
          motion.targetPosition.y - motion.displayPosition.y
        );

        samplesToAppend.push({
          capturedAt: new Date().toISOString(),
          sessionId,
          playerId: id,
          snapshotAgeMs,
          extrapolationMs,
          velocityX: motion.velocity.x,
          velocityY: motion.velocity.y,
          velocityMagnitude,
          displayToTargetGap,
          isMoving: renderStates.get(id)?.isMoving ?? false,
          displayX: motion.displayPosition.x,
          displayY: motion.displayPosition.y,
          targetX: motion.targetPosition.x,
          targetY: motion.targetPosition.y,
          interpolationSpeed: remoteInterpolationSpeed,
          extrapolationLimitMs: remoteExtrapolationLimitMs,
          extrapolationStaleMs: remoteExtrapolationStaleMs,
        });
      }

      if (samplesToAppend.length === 0) {
        return;
      }

      const next = remoteMotionDebugLogRef.current.concat(samplesToAppend);
      if (next.length > MAX_REMOTE_MOTION_DEBUG_SAMPLES) {
        remoteMotionDebugLogRef.current = next.slice(next.length - MAX_REMOTE_MOTION_DEBUG_SAMPLES);
      } else {
        remoteMotionDebugLogRef.current = next;
      }

      setRemoteMotionSampleCount(remoteMotionDebugLogRef.current.length);
      (window as Window & { __remoteMotionDebugLog?: RemoteMotionDebugSample[] }).__remoteMotionDebugLog =
        remoteMotionDebugLogRef.current;
    };

    appendSamples();
    const intervalId = window.setInterval(appendSamples, REMOTE_MOTION_RECORD_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    isRemoteMotionRecording,
    remoteExtrapolationLimitMs,
    remoteExtrapolationStaleMs,
    remoteInterpolationSpeed,
    sessionId
  ]);

  useEffect(() => {
    isPlayerImmobilizedRef.current = !!currentPlayer?.isImmobilized;
  }, [currentPlayer?.isImmobilized]);

  const startGrenadeCharge = useCallback(() => {
    const {
      currentPlayer: stateCurrentPlayer,
      currentPlayerId: stateCurrentPlayerId,
      players: storePlayers,
      grenadeChargeStartTime: activeChargeStartTime,
      setGrenadeChargeStartTime: setChargeStartTime
    } = useGameStore.getState();

    const resolvedPlayerId = stateCurrentPlayer?.id || stateCurrentPlayerId || 'current-player';
    const activePlayer = stateCurrentPlayer || storePlayers.find(player => player.id === resolvedPlayerId);

    if (!activePlayer || activePlayer.isImmobilized || (activePlayer.grenadeCharges || 0) <= 0) {
      return false;
    }

    if (activeChargeStartTime !== null) {
      return true;
    }

    setChargeStartTime(Date.now());
    return true;
  }, []);

  const cancelGrenadeCharge = useCallback(() => {
    const {
      grenadeChargeStartTime: activeChargeStartTime,
      setGrenadeChargeStartTime: setChargeStartTime
    } = useGameStore.getState();

    if (activeChargeStartTime === null) {
      return false;
    }

    setChargeStartTime(null);
    return true;
  }, []);

  const releaseGrenadeCharge = useCallback(() => {
    const {
      grenadeChargeStartTime: activeChargeStartTime,
      setGrenadeChargeStartTime: setChargeStartTime,
      throwGrenade,
      currentPlayer: stateCurrentPlayer,
      currentPlayerId: stateCurrentPlayerId,
      players: storePlayers
    } = useGameStore.getState();

    if (activeChargeStartTime === null) {
      return null;
    }

    const chargeAmount = getGrenadeChargeAmount(Date.now() - activeChargeStartTime);
    const resolvedPlayerId = stateCurrentPlayer?.id || stateCurrentPlayerId || 'current-player';
    const activePlayer = stateCurrentPlayer || storePlayers.find(player => player.id === resolvedPlayerId);

    setChargeStartTime(null);

    if (!activePlayer || activePlayer.isImmobilized) {
      return null;
    }

    const throwDirection = facingDirectionRef.current === 'left' ? { x: -1, y: 0 } : { x: 1, y: 0 };
    return throwGrenade(resolvedPlayerId, chargeAmount, throwDirection, playerPositionRef.current);
  }, []);

  // Legacy AI state variables removed - now using multiple AI system

  // Helper function to create individual AI player state - random positions within starting box
  const getAiStartPosition = (index: number) => {
    // Use index as seed for consistent but varied positioning
    const seedX = Math.sin(index * 2.5) * 0.5 + 0.5; // Converts to 0-1 range
    const seedY = Math.cos(index * 1.7) * 0.5 + 0.5; // Converts to 0-1 range

    // Calculate random position within starting box boundaries
    const minX = STARTING_BOX_X + 10; // 10px padding from box edge
    const maxX = STARTING_BOX_X + STARTING_BOX_WIDTH - CHARACTER_SIZE - 10;
    const minY = STARTING_BOX_Y + 10;
    const maxY = STARTING_BOX_Y + STARTING_BOX_HEIGHT - CHARACTER_SIZE - 10;

    return {
      x: minX + (maxX - minX) * seedX,
      y: minY + (maxY - minY) * seedY
    };
  };

  const createAiPlayerState = useCallback((id: string, index: number): AiPlayerState => {
    const startPosition = getAiStartPosition(index);
    const speedModeId = DEFAULT_SPEED_MODE_ID;
    const baseSpeed = CharacterSpeedManager.getBaseSpeed(speedModeId);
    return {
      id,
      position: startPosition,
      selectedChoice: null,
      isMoving: false,
      facingDirection: 'right',
      targetAnswer: null,
      moveStartTime: 0,
      decisionMade: false,
      accuracy: 0.7 + (Math.random() * 0.3), // Random accuracy between 0.7 and 1.0
      responseDelay: 500 + (Math.random() * 2000), // Random delay between 0.5-2.5 seconds
      speedModeId,
      baseSpeed,
      speedBoostMultiplier: 1,
      hasActivatedTurbo: false,
      turboExpiresAt: null,
      netCooldownUntil: 0,
      lastNetAttemptAt: 0,
      grenadeCooldownUntil: 0,
      lastGrenadeAttemptAt: 0
    };
  }, []);

  // Helper function to initialize AI players from game store
  const initializeAiPlayersFromStore = useCallback(() => {
    const currentPlayers = useGameStore.getState().players;
    const aiPlayerIds = currentPlayers
      .filter(p => p.id.startsWith('ai-player-'))
      .map(p => p.id);

    const newAiPlayers = aiPlayerIds.map((id, index) =>
      createAiPlayerState(id, index)
    );

    setAiPlayers(newAiPlayers);
    newAiPlayers.forEach(aiPlayer => {
      updatePlayerPosition(aiPlayer.id, aiPlayer.position);
    });
  }, [createAiPlayerState, updatePlayerPosition]);

  const storeCurrentQuestion = getCurrentQuestion();
  const sessionQuestion = activeSession?.questions?.[activeSession.currentQuestionIndex ?? 0];
  const currentQuestion = storeCurrentQuestion ?? sessionQuestion ?? null;
  const fallbackRunningSeconds = (() => {
    const configuredDuration = activeSession?.currentPhaseDurationSeconds;
    if (typeof configuredDuration === 'number' && configuredDuration > 0) {
      return configuredDuration;
    }

    const runningDuration = currentQuestion?.arenaPlayTimeSeconds;
    if (typeof runningDuration === 'number' && runningDuration > 0) {
      return runningDuration;
    }

    return DEFAULT_TIMER_CONFIG.runningPhaseSeconds;
  })();

  const runningTimeRemaining = useSessionPhaseTimer(activeSession, fallbackRunningSeconds, {
    resetKey: roundStartTime
  });

  useEffect(() => {
    if (!isAiMode) {
      aiRunningAdvanceRef.current = false;
      return;
    }

    if (runningTimeRemaining > 0) {
      aiRunningAdvanceRef.current = false;
      return;
    }

    if (aiRunningAdvanceRef.current) {
      return;
    }

    aiRunningAdvanceRef.current = true;
    setGameState('scoring');
    navigate('/scores');
  }, [isAiMode, runningTimeRemaining, setGameState, navigate]);
  const storeTotalQuestions = getTotalQuestions();
  const totalQuestions = storeTotalQuestions > 0
    ? storeTotalQuestions
    : activeSession?.questions?.length ?? 0;
  const resolvedQuestionIndex = storeTotalQuestions > 0
    ? currentQuestionIndex
    : activeSession?.currentQuestionIndex ?? 0;

  useLayoutEffect(() => {
    if (!isSpectator || !currentQuestion) {
      setSpectatorHeaderHeight(0);
      return;
    }

    const updateHeaderHeight = () => {
      if (!headerRef.current) {
        setSpectatorHeaderHeight(0);
        return;
      }

      const measuredHeight = headerRef.current.getBoundingClientRect().height;
      setSpectatorHeaderHeight(prev => (prev !== measuredHeight ? measuredHeight : prev));
    };

    updateHeaderHeight();

    let resizeObserver: ResizeObserver | null = null;
    const handleWindowResize = () => updateHeaderHeight();

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => updateHeaderHeight());
      if (headerRef.current) {
        resizeObserver.observe(headerRef.current);
      }
    } else if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleWindowResize);
    }

    return () => {
      resizeObserver?.disconnect();
      if (!resizeObserver && typeof window !== 'undefined') {
        window.removeEventListener('resize', handleWindowResize);
      }
    };
  }, [isSpectator, currentQuestion]);

  const computeWorldTransform = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const viewportWidth = window.innerWidth;
    const verticalOffsetBase = isSpectator
      ? spectatorHeaderHeight + HEADER_VERTICAL_BUFFER
      : 0;
    const availableHeightRaw = window.innerHeight - verticalOffsetBase;
    const availableHeight = Math.max(availableHeightRaw, 1);
    const rawScale = Math.min(
      viewportWidth / WORLD_WIDTH,
      availableHeight / WORLD_HEIGHT
    );
    const scale = Math.min(rawScale, MAX_WORLD_SCALE);
    const scaledWidth = WORLD_WIDTH * scale;
    const scaledHeight = WORLD_HEIGHT * scale;
    const offsetX = (viewportWidth - scaledWidth) / 2;
    const offsetY = verticalOffsetBase + Math.max((availableHeight - scaledHeight) / 2, 0);

    setWorldScale(scale);
    setWorldOffset({ x: offsetX, y: offsetY });
  }, [isSpectator, spectatorHeaderHeight]);

  // Auto-navigate when timer expires or game state changes - DISABLED: Now handled by StudentAutoNavigation
  // useEffect(() => {
  //   if (gameState === 'scoring') {
  //     navigate('/scores');
  //   }
  // }, [gameState, navigate]);

  // Dynamic viewport sizing with fixed world dimensions
  useEffect(() => {
    computeWorldTransform();

    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('resize', computeWorldTransform);
    const visualViewport = window.visualViewport;
    visualViewport?.addEventListener('resize', computeWorldTransform);

    return () => {
      window.removeEventListener('resize', computeWorldTransform);
      visualViewport?.removeEventListener('resize', computeWorldTransform);
    };
  }, [computeWorldTransform]);

  // Start local running-phase state only when the session is actually in the arena.
  useEffect(() => {
    const shouldInitializeRunningPhase =
      isAiMode ||
      (!sessionId && !activeSession) ||
      activeSession?.gameState === 'running';

    if (!shouldInitializeRunningPhase || hasInitializedArenaRef.current) {
      return;
    }

    hasInitializedArenaRef.current = true;

    // IMPORTANT: Initialize character speed BEFORE setting game state to 'running'
    // This ensures characterSpeedData exists when applyRoundBasedItemEffects is called
    // (which happens inside setGameState when transitioning to 'running')
    const store = useGameStore.getState();
    if (!store.characterSpeedData) {
      store.initializeCharacterSpeed(0);
    }

    setGameState('running');
    startNewRound(); // Initialize round timing for score calculation

    const effectivePlayerId = !isSpectator && playerId
      ? playerId
      : resolvedCurrentPlayerId;

    // Sync initial player position to game store (only if not already set in multiplayer session)
    if (activeSession && activeSession.players) {
      // In multiplayer: use position from session (already set when player joined)
      const sessionPlayer = activeSession.players.find(p => p.id === effectivePlayerId);
      if (sessionPlayer && sessionPlayer.position) {
        updatePlayerPosition(effectivePlayerId, sessionPlayer.position);
      } else {
        // Fallback if session player not found
        updatePlayerPosition(effectivePlayerId, { x: PLAYER_START_X, y: PLAYER_START_Y });
      }
    } else {
      // In AI mode: use calculated starting position
      updatePlayerPosition(effectivePlayerId, { x: PLAYER_START_X, y: PLAYER_START_Y });
    }

    if (isAiMode) {
      // Check if AI players already exist in store
      const currentPlayers = useGameStore.getState().players;
      const existingAiCount = currentPlayers.filter(p => p.id.startsWith('ai-player-')).length;

      if (existingAiCount === 0) {
        // Add multiple AI players using the configured count
        addMultipleAiPlayers(aiPlayerCount);

        // Initialize AI player states after a brief delay to ensure store is updated
        setTimeout(() => {
          initializeAiPlayersFromStore();
        }, 100);
      } else {
        initializeAiPlayersFromStore();
      }
    } else {
      setAiPlayers([]);
    }

    // Show player arrow for 1.5 seconds at game start
    setShowPlayerArrow(true);
    const arrowTimer = setTimeout(() => {
      setShowPlayerArrow(false);
    }, 1500);

    return () => clearTimeout(arrowTimer);
    // Intentionally narrow: re-initialize only when the session identity or phase
    // changes. Depending on the whole `activeSession` would re-fire arena setup on
    // every player-position/score snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    addMultipleAiPlayers,
    aiPlayerCount,
    activeSession?.gameState,
    activeSession?.id,
    initializeAiPlayersFromStore,
    isAiMode,
    isSpectator,
    playerId,
    resolvedCurrentPlayerId,
    sessionId,
    setGameState,
    startNewRound,
    updatePlayerPosition
  ]);

  useEffect(() => {
    if (gameState !== 'running') {
      return;
    }

    if (!currentPlayer?.shoesEffectNextRound && !hasShoesItem) {
      return;
    }

    if (hasActiveShoesModifier) {
      return;
    }

    applyRoundBasedItemEffects();
  }, [
    gameState,
    currentPlayer?.shoesEffectNextRound,
    hasShoesItem,
    hasActiveShoesModifier,
    applyRoundBasedItemEffects
  ]);

  // Ctrl+Shift+D toggles remote motion debug overlay.
  useEffect(() => {
    const handleDebugToggle = (event: KeyboardEvent) => {
      if (!event.ctrlKey || !event.shiftKey || event.key.toLowerCase() !== 'd') {
        return;
      }

      event.preventDefault();
      setShowRemoteMotionDebug(prev => !prev);
    };

    window.addEventListener('keydown', handleDebugToggle);
    return () => {
      window.removeEventListener('keydown', handleDebugToggle);
    };
  }, []);

  // Keyboard control system
  useEffect(() => {
    // Skip keyboard controls in spectator mode
    if (isSpectator) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const key = event.key;

      // Tab key - Show question overlay
      if (key === 'Tab') {
        event.preventDefault();
        updateQuestionOverlayVisibility({ type: 'tab-down' });
        return;
      }

      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) {
        event.preventDefault();
        setKeysPressed(prev => new Set([...prev, key]));
        setIsMoving(true);

        // Update facing direction
        if (key === 'ArrowLeft') {
          setFacingDirection('left');
          facingDirectionRef.current = 'left';
        }
        if (key === 'ArrowRight') {
          setFacingDirection('right');
          facingDirectionRef.current = 'right';
        }
      } else if (key === ' ' || key === 'Space') {
        event.preventDefault();
        handlePrimaryAction();
      } else if (key === 'Shift') {
        event.preventDefault();
        startGrenadeCharge();
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      const key = event.key;

      // Tab key - Hide question overlay
      if (key === 'Tab') {
        event.preventDefault();
        updateQuestionOverlayVisibility({ type: 'tab-up' });
        return;
      }

      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) {
        setKeysPressed(prev => {
          const newSet = new Set(prev);
          newSet.delete(key);
          return newSet;
        });
      } else if (key === 'Shift') {
        event.preventDefault();
        releaseGrenadeCharge();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isSpectator, releaseGrenadeCharge, startGrenadeCharge, handlePrimaryAction, updateQuestionOverlayVisibility]);

  // Combined movement loop for player and AI with fixed timestep scaling
  useEffect(() => {
    if (gameState !== 'running') {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      lastFrameTimeRef.current = null;
      setIsMoving(false);
      return;
    }

    const step = (timestamp: number) => {
      if (lastFrameTimeRef.current === null) {
        lastFrameTimeRef.current = timestamp;
      }

      let deltaSeconds = (timestamp - lastFrameTimeRef.current) / 1000;
      lastFrameTimeRef.current = timestamp;
      deltaSeconds = Math.min(deltaSeconds, MAX_FRAME_STEP_SECONDS);

      if (deltaSeconds > 0) {
        const keys = keysPressedRef.current;
        const joystick = joystickDirectionRef.current;
        let moveX = 0;
        let moveY = 0;

        // Joystick takes priority over keyboard (analog input)
        if (Math.abs(joystick.x) > 0 || Math.abs(joystick.y) > 0) {
          moveX = joystick.x;
          moveY = joystick.y;
        } else {
          if (keys.has('ArrowLeft')) moveX -= 1;
          if (keys.has('ArrowRight')) moveX += 1;
          if (keys.has('ArrowUp')) moveY -= 1;
          if (keys.has('ArrowDown')) moveY += 1;
        }

        const moving = moveX !== 0 || moveY !== 0;
        const isPlayerImmobilized = isPlayerImmobilizedRef.current;
        const playerSpeedPerSecond = getCurrentCharacterSpeed() * TARGET_FRAME_RATE;
        const currentPlayerData = useGameStore.getState().currentPlayer;
        const isBeingPushed = currentPlayerData?.isBeingPushed;
        const pushVelocity = currentPlayerData?.pushVelocity;
        const previousPlayerPosition = playerPositionRef.current;
        let nextPlayerPosition = previousPlayerPosition;

        if (isBeingPushed && pushVelocity) {
          const pushStepX = pushVelocity.x * deltaSeconds;
          const pushStepY = pushVelocity.y * deltaSeconds;

          nextPlayerPosition = {
            x: Math.max(20, Math.min(WORLD_WIDTH - CHARACTER_SIZE - 20, previousPlayerPosition.x + pushStepX)),
            y: Math.max(120, Math.min(WORLD_HEIGHT - CHARACTER_SIZE - 20, previousPlayerPosition.y + pushStepY))
          };

          if (pushStepX !== 0) {
            const newFacingDirection = pushStepX > 0 ? 'right' : 'left';
            if (facingDirectionRef.current !== newFacingDirection) {
              facingDirectionRef.current = newFacingDirection;
              setFacingDirection(newFacingDirection);
            }
          }
        } else if (moving && !isPlayerImmobilized) {
          const magnitude = Math.sqrt(moveX * moveX + moveY * moveY);
          const stepX = (moveX / magnitude) * playerSpeedPerSecond * deltaSeconds;
          const stepY = (moveY / magnitude) * playerSpeedPerSecond * deltaSeconds;

          const newX = Math.max(20, Math.min(WORLD_WIDTH - CHARACTER_SIZE - 20, previousPlayerPosition.x + stepX));
          const newY = Math.max(120, Math.min(WORLD_HEIGHT - CHARACTER_SIZE - 20, previousPlayerPosition.y + stepY));

          if (newX !== previousPlayerPosition.x || newY !== previousPlayerPosition.y) {
            nextPlayerPosition = { x: newX, y: newY };

            if (stepX !== 0) {
              const newFacingDirection = stepX > 0 ? 'right' : 'left';
              if (facingDirectionRef.current !== newFacingDirection) {
                facingDirectionRef.current = newFacingDirection;
                setFacingDirection(newFacingDirection);
              }
            }
          }
        }

        if (
          nextPlayerPosition.x !== previousPlayerPosition.x ||
          nextPlayerPosition.y !== previousPlayerPosition.y
        ) {
          playerPositionRef.current = nextPlayerPosition;
          setPlayerPosition(nextPlayerPosition);
          updatePlayerPosition(resolvedCurrentPlayerId, nextPlayerPosition);
        }
        setIsMoving(moving);

        const storeSnapshot = useGameStore.getState();
        const storePlayers = storeSnapshot.players;
        const nonAiPlayers = storePlayers.filter(player => !player.id.startsWith('ai-player-'));
        const previousAiPlayers = aiPlayersRef.current;
        if (previousAiPlayers.length > 0) {
          let hasChanges = false;
          const now = Date.now();
          const aiPositionUpdates: Array<{ id: string; position: { x: number; y: number } }> = [];

          const updatedAiPlayers = previousAiPlayers.map(aiPlayer => {
            const aiPlayerData = storePlayers.find(p => p.id === aiPlayer.id);
            if (!aiPlayerData) {
              return aiPlayer;
            }

            const updates: Partial<AiPlayerState> = {};
            let newPosition: { x: number; y: number } | null = null;
            let newFacing: 'left' | 'right' = aiPlayer.facingDirection;
            const aiCenterX = aiPlayer.position.x + CHARACTER_SIZE / 2;
            const aiCenterY = aiPlayer.position.y + CHARACTER_SIZE / 2;

            const baseMultiplier = aiPlayerData.shoesEffectNextRound ? SHOES_MULTIPLIER : 1;
            let turboActive = aiPlayer.hasActivatedTurbo;
            let turboExpiresAt = aiPlayer.turboExpiresAt ?? null;

            if (turboActive && turboExpiresAt && turboExpiresAt <= now) {
              turboActive = false;
              turboExpiresAt = null;
            }

            if (
              !turboActive &&
              aiPlayerData.turboAvailable &&
              !aiPlayerData.turboUsed &&
              aiPlayer.isMoving &&
              typeof storeSnapshot.activateTurboItem === 'function'
            ) {
              const elapsed = now - aiPlayer.moveStartTime;
              if (elapsed >= TURBO_TRIGGER_WINDOW.min && elapsed <= TURBO_TRIGGER_WINDOW.max) {
                if (storeSnapshot.activateTurboItem(aiPlayer.id)) {
                  turboActive = true;
                  turboExpiresAt = now + TURBO_DURATION_MS;
                }
              }
            }

            let speedMultiplier = baseMultiplier;
            if (turboActive && turboExpiresAt && turboExpiresAt > now) {
              speedMultiplier *= TURBO_MULTIPLIER;
            }

            if (turboActive !== aiPlayer.hasActivatedTurbo) {
              updates.hasActivatedTurbo = turboActive;
            }
            if ((turboExpiresAt ?? null) !== (aiPlayer.turboExpiresAt ?? null)) {
              updates.turboExpiresAt = turboExpiresAt;
            }
            if (speedMultiplier !== aiPlayer.speedBoostMultiplier) {
              updates.speedBoostMultiplier = speedMultiplier;
            }

            let targetPlayer: Player | null = null;
            let dxToTarget = 0;
            let dyToTarget = 0;
            let distanceToTarget = Infinity;

            if (!aiPlayerData.isImmobilized && nonAiPlayers.length > 0) {
              let closestDistanceSq = Infinity;
              for (const candidate of nonAiPlayers) {
                if (candidate.id === aiPlayer.id) {
                  continue;
                }
                const candidateCenterX = candidate.position.x + CHARACTER_SIZE / 2;
                const candidateCenterY = candidate.position.y + CHARACTER_SIZE / 2;
                const dx = candidateCenterX - aiCenterX;
                const dy = candidateCenterY - aiCenterY;
                const distanceSq = dx * dx + dy * dy;

                if (distanceSq < closestDistanceSq) {
                  closestDistanceSq = distanceSq;
                  targetPlayer = candidate;
                  dxToTarget = dx;
                  dyToTarget = dy;
                  distanceToTarget = Math.sqrt(distanceSq);
                }
              }
            }

            if (targetPlayer && Math.abs(dxToTarget) > 10) {
              const desiredFacing = dxToTarget > 0 ? 'right' : 'left';
              if (desiredFacing !== newFacing) {
                newFacing = desiredFacing;
              }
            }

            let controlItemUsed = false;
            if (
              targetPlayer &&
              (aiPlayerData.netCharges ?? 0) > 0 &&
              now >= aiPlayer.netCooldownUntil &&
              now - aiPlayer.lastNetAttemptAt > 600
            ) {
              const facingTarget = (newFacing === 'right' && dxToTarget > 0) || (newFacing === 'left' && dxToTarget < 0);
              if (
                facingTarget &&
                distanceToTarget <= NET_EFFECTIVE_RANGE &&
                Math.abs(dyToTarget) <= NET_VERTICAL_TOLERANCE &&
                !(targetPlayer?.isImmobilized) &&
                typeof storeSnapshot.throwNet === 'function'
              ) {
                const magnitude = Math.max(distanceToTarget, 1);
                const netDirection = {
                  x: dxToTarget / magnitude,
                  y: dyToTarget / magnitude
                };
                const netId = storeSnapshot.throwNet(aiPlayer.id, netDirection, aiPlayer.position);
                if (netId) {
                  updates.netCooldownUntil = now + NET_COOLDOWN_MS;
                  updates.lastNetAttemptAt = now;
                  controlItemUsed = true;
                }
              }
            }

            if (
              !controlItemUsed &&
              targetPlayer &&
              (aiPlayerData.grenadeCharges ?? 0) > 0 &&
              now >= aiPlayer.grenadeCooldownUntil &&
              now - aiPlayer.lastGrenadeAttemptAt > 1000 &&
              Math.abs(dxToTarget) >= GRENADE_MIN_RANGE &&
              Math.abs(dxToTarget) <= GRENADE_MAX_RANGE &&
              typeof storeSnapshot.throwGrenade === 'function'
            ) {
              const horizontalDistanceToTarget = Math.abs(dxToTarget);
              const throwDirection = {
                x: dxToTarget >= 0 ? 1 : -1,
                y: 0
              };
              const normalizedDistance = Math.min(1, Math.max(GRENADE_MIN_CHARGE, horizontalDistanceToTarget / GRENADE_MAX_RANGE));
              const grenadeId = storeSnapshot.throwGrenade(aiPlayer.id, normalizedDistance, throwDirection, aiPlayer.position);
              if (grenadeId) {
                updates.grenadeCooldownUntil = now + GRENADE_COOLDOWN_MS;
                updates.lastGrenadeAttemptAt = now;
              }
            }

            if (aiPlayerData.isBeingPushed && aiPlayerData.pushVelocity) {
              const pushStepX = aiPlayerData.pushVelocity.x * deltaSeconds;
              const pushStepY = aiPlayerData.pushVelocity.y * deltaSeconds;
              const newX = Math.max(20, Math.min(WORLD_WIDTH - CHARACTER_SIZE - 20, aiPlayer.position.x + pushStepX));
              const newY = Math.max(120, Math.min(WORLD_HEIGHT - CHARACTER_SIZE - 20, aiPlayer.position.y + pushStepY));
              newPosition = { x: newX, y: newY };
              if (pushStepX !== 0) {
                newFacing = pushStepX > 0 ? 'right' : 'left';
              }
            } else if (aiPlayerData.isImmobilized || !aiPlayer.isMoving || aiPlayer.targetAnswer === null || aiPlayer.selectedChoice !== null) {
              // Immobilized or no movement required
            } else {
              const answerCount = currentQuestion?.choices.length ?? 0;
              const { centerX: circleX, centerY: targetY } = getAnswerCirclePosition(aiPlayer.targetAnswer, answerCount);

              const dx = circleX - aiCenterX;
              const dy = targetY - aiCenterY;
              const distance = Math.sqrt(dx * dx + dy * dy);

              if (distance < 20) {
                const holdTime = now - aiPlayer.moveStartTime;
                if (holdTime > 1500 + Math.random() * 2000) {
                  updates.selectedChoice = aiPlayer.targetAnswer;
                  updates.isMoving = false;
                }
              } else if (distance > 0 && aiPlayer.baseSpeed > 0) {
                const effectiveMultiplier = updates.speedBoostMultiplier ?? aiPlayer.speedBoostMultiplier ?? 1;
                const aiSpeedPerSecond = aiPlayer.baseSpeed * effectiveMultiplier * TARGET_FRAME_RATE;
                const moveScalar = aiSpeedPerSecond * deltaSeconds;
                const baseMoveX = (dx / distance) * moveScalar;
                const baseMoveY = (dy / distance) * moveScalar;

                const jitterMagnitude = moveScalar * 0.1;
                const jitterX = (Math.random() - 0.5) * jitterMagnitude;
                const jitterY = (Math.random() - 0.5) * jitterMagnitude;

                const stepX = baseMoveX + jitterX;
                const stepY = baseMoveY + jitterY;

                const newX = Math.max(20, Math.min(WORLD_WIDTH - CHARACTER_SIZE - 20, aiPlayer.position.x + stepX));
                const newY = Math.max(120, Math.min(WORLD_HEIGHT - CHARACTER_SIZE - 20, aiPlayer.position.y + stepY));

                if (newX !== aiPlayer.position.x || newY !== aiPlayer.position.y) {
                  newPosition = { x: newX, y: newY };
                }
                if (stepX !== 0) {
                  newFacing = stepX > 0 ? 'right' : 'left';
                }
              } else {
                updates.isMoving = false;
              }
            }

            if (newPosition && (newPosition.x !== aiPlayer.position.x || newPosition.y !== aiPlayer.position.y)) {
              updates.position = newPosition;
              aiPositionUpdates.push({ id: aiPlayer.id, position: newPosition });
            }

            if (newFacing !== aiPlayer.facingDirection) {
              updates.facingDirection = newFacing;
            }

            if (Object.keys(updates).length > 0) {
              hasChanges = true;
              return { ...aiPlayer, ...updates };
            }

            return aiPlayer;
          });

          if (hasChanges) {
            aiPlayersRef.current = updatedAiPlayers;
            setAiPlayers(updatedAiPlayers);
            aiPositionUpdates.forEach(({ id, position }) => {
              updatePlayerPosition(id, position);
            });
          }
        }

        // Update net projectiles
        updateNetProjectiles(deltaSeconds);

        // Update grenade projectiles
        updateGrenadeProjectiles(deltaSeconds);

        // Update player immobilization status
        updatePlayerImmobilization();

        // Update player push effects
        updatePlayerPushEffects();
      }

      animationFrameRef.current = requestAnimationFrame(step);
    };

    animationFrameRef.current = requestAnimationFrame(step);

    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      lastFrameTimeRef.current = null;
    };
  }, [gameState, getCurrentCharacterSpeed, updateNetProjectiles, updatePlayerImmobilization, resolvedCurrentPlayerId, updateGrenadeProjectiles, updatePlayerPosition, updatePlayerPushEffects, currentQuestion?.choices.length]);

  // Multiple AI Decision Making System
  useEffect(() => {
    if (gameState === 'running' && currentQuestion && aiPlayers.length > 0) {
      // Make decisions for each AI player individually
      aiPlayers.forEach((aiPlayer) => {
        if (!aiPlayer.decisionMade) {
          const decisionDelay = aiPlayer.responseDelay; // Use individual AI delay

          setTimeout(() => {
            // Individual AI decision logic based on their accuracy
            let targetAnswer: number;
            if (Math.random() < aiPlayer.accuracy) {
              targetAnswer = currentQuestion.correctAnswerIndex;
            } else {
              // Pick a random incorrect answer
              const incorrectAnswers = [0, 1, 2, 3].filter(i => i !== currentQuestion.correctAnswerIndex && i < currentQuestion.choices.length);

              if (incorrectAnswers.length > 0) {
                targetAnswer = incorrectAnswers[Math.floor(Math.random() * incorrectAnswers.length)];
              } else {
                // Fallback if no incorrect answers available (shouldn't happen)
                targetAnswer = currentQuestion.correctAnswerIndex;
              }
            }

            // Update the specific AI player state
            setAiPlayers(prevAiPlayers =>
              prevAiPlayers.map(ai =>
                ai.id === aiPlayer.id
                  ? {
                      ...ai,
                      targetAnswer,
                      moveStartTime: Date.now(),
                      decisionMade: true,
                      isMoving: true
                    }
                  : ai
              )
            );

            // Also record the response for scoring immediately
            const responseTime = Date.now() - roundStartTime + (Math.random() - 0.5) * 1000;
            recordPlayerResponse(aiPlayer.id, targetAnswer, Math.max(500, responseTime));

          }, decisionDelay);
        }
      });
    }
  }, [gameState, currentQuestion, aiPlayers, roundStartTime, recordPlayerResponse]);
  // Reset all AI players when question changes
  useEffect(() => {
    setAiPlayers(prevAiPlayers =>
      prevAiPlayers.map((aiPlayer, index) => ({
        ...aiPlayer,
        selectedChoice: null,
        targetAnswer: null,
        decisionMade: false,
        isMoving: false,
        position: getAiStartPosition(index),
        moveStartTime: 0,
        speedBoostMultiplier: 1,
        hasActivatedTurbo: false,
        turboExpiresAt: null,
        netCooldownUntil: 0,
        lastNetAttemptAt: 0,
        grenadeCooldownUntil: 0,
        lastGrenadeAttemptAt: 0
      }))
    );
  }, [resolvedQuestionIndex]);

  // Enhanced answer selection with timer-based confirmation
  useEffect(() => {
    if (currentQuestion) {
      const answerZones = currentQuestion.choices.map((_, index) => {
        const { centerX, centerY } = getAnswerCirclePosition(index, currentQuestion.choices.length);

        return {
          index,
          x: centerX,
          y: centerY
        };
      });

      let currentHovered = null;
      let currentConsidering = null;

      answerZones.forEach(zone => {
        if (zone.index >= currentQuestion.choices.length) return;

        const distance = Math.sqrt(
          Math.pow((playerPosition.x + CHARACTER_SIZE/2) - zone.x, 2) +
          Math.pow((playerPosition.y + CHARACTER_SIZE/2) - zone.y, 2)
        );

        // Stage 1: Hover detection (wider radius for visual feedback)
        if (distance < ANSWER_HOVER_RADIUS) {
          currentHovered = zone.index;
        }

        // Stage 2: Precise positioning for consideration (larger lock-in radius for easier activation)
        if (distance < ANSWER_LOCK_IN_RADIUS) {
          currentConsidering = zone.index;
        }
      });

      // Update hover state
      setHoveredChoice(currentHovered);

      // Handle consideration state changes
      if (currentConsidering !== consideringChoice) {
        setConsideringChoice(currentConsidering);
        setSelectionTimer(0); // Reset timer when changing consideration
      }
    }
  }, [playerPosition, currentQuestion, consideringChoice]);

  useEffect(() => {
    if (gameState !== 'running') {
      return;
    }

    setSelectedChoice(null);
    setHoveredChoice(null);
    setConsideringChoice(null);
    setSelectionTimer(0);
  }, [gameState, resolvedQuestionIndex]);

  // Timer-based selection confirmation with answer changing capability
  useEffect(() => {
    if (gameState !== 'running') {
      setSelectionTimer(0);
      return;
    }

    if (consideringChoice !== null && currentQuestion) {
      const startTime = Date.now();
      setSelectionTimer(0);

      // Use a single timeout instead of frequent intervals
      const confirmationTimeout = setTimeout(() => {
        if (consideringChoiceRef.current !== consideringChoice) {
          return;
        }

        const { centerX, centerY } = getAnswerCirclePosition(consideringChoice, currentQuestion.choices.length);
        const playerCenterX = playerPositionRef.current.x + (CHARACTER_SIZE / 2);
        const playerCenterY = playerPositionRef.current.y + (CHARACTER_SIZE / 2);
        const distanceToCenter = Math.hypot(playerCenterX - centerX, playerCenterY - centerY);

        if (distanceToCenter >= ANSWER_LOCK_IN_RADIUS) {
          return;
        }

        setSelectedChoice(consideringChoice);
        selectAnswer(consideringChoice);

        // Play answer selection sound
        if (audioManager.isReady()) {
          audioManager.play(PLAYER_SOUNDS.ANSWER_SELECT.id);
        }

        // Record response with timing for scoring system
        const responseTime = Date.now() - roundStartTime;
        recordPlayerResponse(resolvedCurrentPlayerId, consideringChoice, responseTime);

        setSelectionTimer(200);
      }, 200);

      // Use requestAnimationFrame for smooth progress updates instead of setInterval
      let animationFrame: number;
      const updateProgress = () => {
        const elapsed = Date.now() - startTime;
        if (elapsed < 200) {
          setSelectionTimer(elapsed);
          animationFrame = requestAnimationFrame(updateProgress);
        }
      };
      animationFrame = requestAnimationFrame(updateProgress);

      return () => {
        clearTimeout(confirmationTimeout);
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }
      };
    } else {
      setSelectionTimer(0); // Reset timer when not considering any choice
    }
  }, [
    consideringChoice,
    currentQuestion,
    gameState,
    selectAnswer,
    roundStartTime,
    recordPlayerResponse,
    resolvedCurrentPlayerId
  ]);

  // Allow answer changing by resetting selection state when considering a different choice
  useEffect(() => {
    if (consideringChoice !== null && selectedChoice !== null && consideringChoice !== selectedChoice) {
      setSelectedChoice(null); // Reset selection to allow new choice
      setSelectionTimer(0); // Reset timer for new choice
    }
  }, [consideringChoice, selectedChoice]);

  useEffect(() => {
    if (consideringChoice === null && selectedChoice !== null) {
      setSelectedChoice(null);
      setSelectionTimer(0);
      void clearCurrentPlayerAnswer();
    }
  }, [consideringChoice, selectedChoice, clearCurrentPlayerAnswer]);

  // Initialize with sample questions if none exist (for direct navigation)
  useEffect(() => {
    if (!storeCurrentQuestion && questions.length === 0 && !sessionQuestion && !sessionId) {
      const sampleQuestions = [
        {
          id: '1',
          text: 'What is the capital of France?',
          choices: ['London', 'Berlin', 'Paris', 'Madrid'],
          correctAnswerIndex: 2,
          category: 'Geography',
          difficulty: 'easy' as const
        },
        {
          id: '2',
          text: 'Which planet is closest to the Sun?',
          choices: ['Venus', 'Mercury', 'Earth', 'Mars'],
          correctAnswerIndex: 1,
          category: 'Science',
          difficulty: 'medium' as const
        }
      ];

      // Initialize game with sample questions
      initializeGame('direct-access-session', sampleQuestions);
    }
  }, [storeCurrentQuestion, questions.length, sessionQuestion, initializeGame, sessionId]);

  if (!currentQuestion && questions.length === 0) {
    if (sessionId) {
      return (
        <div style={{
          padding: '2rem',
          textAlign: 'center',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #2563EB 0%, #1E40AF 25%, #059669 75%, #047857 100%)',
          color: 'white'
        }}>
          <div style={{
            backgroundColor: 'rgba(255,255,255,0.1)',
            padding: '2rem 3rem',
            borderRadius: '20px',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            <h2 style={{ marginBottom: '1rem' }}>🔄 Loading arena data...</h2>
            <p style={{ color: 'rgba(255,255,255,0.8)' }}>Fetching the latest question set from the session.</p>
          </div>
        </div>
      );
    }

    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <div style={{
          backgroundColor: 'rgba(255,255,255,0.1)',
          padding: '2rem',
          borderRadius: '20px',
          backdropFilter: 'blur(10px)'
        }}>
          <h2 style={{ marginBottom: '1rem' }}>🎮 Initializing Game Arena...</h2>
          <p>Loading sample questions for direct access</p>
        </div>
      </div>
    );
  }

  const answerColors = ['#F44336', '#2196F3', '#4CAF50', '#FF9800'];
  const questionHeaderColors = [
    { bg: '#E53E3E', border: '#C53030', text: '#FFFFFF' },
    { bg: '#3182CE', border: '#2C5282', text: '#FFFFFF' },
    { bg: '#38A169', border: '#2F855A', text: '#FFFFFF' },
    { bg: '#D69E2E', border: '#B7791F', text: '#FFFFFF' }
  ];
  const spectatorQuestionAvailableWidth = Math.max(spectatorQuestionWindowRect.width - 24, 1);
  const spectatorQuestionAvailableHeight = Math.max(
    spectatorQuestionWindowRect.height - SPECTATOR_QUESTION_TITLE_BAR_HEIGHT - 12,
    1
  );
  const spectatorQuestionLayoutScale = Math.max(
    0.7,
    Math.min(
      spectatorQuestionAvailableWidth / SPECTATOR_QUESTION_WINDOW_BASE_WIDTH,
      spectatorQuestionAvailableHeight / SPECTATOR_QUESTION_WINDOW_BASE_HEIGHT
    )
  );
  const spectatorQuestionPaddingX = Math.round(clampNumber(24 * spectatorQuestionLayoutScale, 12, 30, 22));
  const spectatorQuestionPaddingY = Math.round(clampNumber(12 * spectatorQuestionLayoutScale, 6, 16, 10));
  const spectatorQuestionGap = Math.round(clampNumber(10 * spectatorQuestionLayoutScale, 6, 14, 8));
  const spectatorQuestionTitleFontSize = Math.round(clampNumber(28 * spectatorQuestionLayoutScale, 16, 34, 24));
  const spectatorQuestionChoiceBadgeFontSize = Math.round(clampNumber(22 * spectatorQuestionLayoutScale, 14, 28, 20));
  const spectatorQuestionChoiceTextFontSize = Math.round(clampNumber(16 * spectatorQuestionLayoutScale, 12, 20, 15));
  const spectatorQuestionChoiceMinWidth = Math.round(clampNumber(220 * spectatorQuestionLayoutScale, 130, 230, 180));
  const spectatorQuestionChoiceMinHeight = Math.round(clampNumber(110 * spectatorQuestionLayoutScale, 68, 120, 98));
  const spectatorQuestionTextMaxHeight = Math.round(clampNumber(110 * spectatorQuestionLayoutScale, 48, 130, 88));
  const worldContainerStyle = {
    position: 'absolute',
    left: `${worldOffset.x}px`,
    top: `${worldOffset.y}px`,
    width: `${WORLD_WIDTH}px`,
    height: `${WORLD_HEIGHT}px`,
    transform: `scale(${worldScale})`,
    transformOrigin: 'top left'
  } as React.CSSProperties;

  const remoteMotionDebugRows = showRemoteMotionDebug
    ? Array.from(remotePlayerMotionRef.current.entries())
        .map(([id, motion]) => {
          const snapshotAgeMs = Math.max(0, performance.now() - motion.lastSnapshotTime);
          const extrapolationMs = snapshotAgeMs >= remoteExtrapolationStaleMs
            ? 0
            : Math.min(snapshotAgeMs, remoteExtrapolationLimitMs);
          const velocityMagnitude = Math.hypot(motion.velocity.x, motion.velocity.y);
          const displayToTarget = Math.hypot(
            motion.targetPosition.x - motion.displayPosition.x,
            motion.targetPosition.y - motion.displayPosition.y
          );
          const isMoving = remotePlayerRenderStates.get(id)?.isMoving ?? false;

          return {
            id,
            snapshotAgeMs,
            extrapolationMs,
            velocityX: motion.velocity.x,
            velocityY: motion.velocity.y,
            velocityMagnitude,
            displayToTarget,
            isMoving,
          };
        })
        .sort((a, b) => a.id.localeCompare(b.id))
    : [];
  const remoteMotionRecordingDurationMs = remoteMotionRecordStartedAtMs
    ? Math.max(0, Date.now() - remoteMotionRecordStartedAtMs)
    : 0;
  const remoteMotionRecordingDurationSeconds = remoteMotionRecordingDurationMs / 1000;
  const remoteMotionRecordingDurationText = remoteMotionRecordStartedAtMs
    ? (remoteMotionRecordingDurationSeconds >= 60
      ? `${Math.floor(remoteMotionRecordingDurationSeconds / 60)}m ${Math.floor(remoteMotionRecordingDurationSeconds % 60)}s`
      : `${remoteMotionRecordingDurationSeconds.toFixed(1)}s`)
    : '0.0s';

  return (
    <div style={{
      height: '100vh',
      width: '100vw',
      backgroundColor: '#2E7D32',
      position: 'relative',
      overflow: 'hidden',
      backgroundImage: 'linear-gradient(90deg, rgba(46,125,50,1) 0%, rgba(46,125,50,0.8) 50%, rgba(76,175,80,1) 100%)',
      touchAction: 'none',
      WebkitUserSelect: 'none',
      userSelect: 'none',
    } as React.CSSProperties}>
      <MobileGameOverlay />
      {showRemoteMotionDebug && (
        <div
          style={{
            position: 'fixed',
            top: isSpectator ? Math.max(16, spectatorHeaderHeight + 16) : 16,
            right: 16,
            width: 'min(440px, calc(100vw - 24px))',
            maxHeight: '70vh',
            overflowY: 'auto',
            zIndex: 120,
            borderRadius: '12px',
            border: '1px solid rgba(148, 163, 184, 0.45)',
            background: 'rgba(15, 23, 42, 0.92)',
            color: '#E2E8F0',
            boxShadow: '0 14px 34px rgba(0,0,0,0.42)',
            backdropFilter: 'blur(6px)',
            padding: '12px'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Remote Motion Debug</div>
            <button
              onClick={() => setShowRemoteMotionDebug(false)}
              style={{
                border: '1px solid rgba(148, 163, 184, 0.45)',
                borderRadius: 8,
                background: 'rgba(30, 41, 59, 0.9)',
                color: '#E2E8F0',
                padding: '4px 8px',
                cursor: 'pointer',
                fontSize: 12
              }}
            >
              Close
            </button>
          </div>

          <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 6 }}>
            Toggle: Ctrl+Shift+D | Tracked players: {remoteMotionDebugRows.length}
          </div>

          <div
            style={{
              marginTop: 8,
              display: 'grid',
              gap: 8,
              border: '1px solid rgba(148, 163, 184, 0.25)',
              borderRadius: 8,
              background: 'rgba(15, 23, 42, 0.5)',
              padding: '8px 9px'
            }}
          >
            <div style={{ fontSize: 12, color: '#CBD5E1' }}>
              Recorder: {isRemoteMotionRecording ? 'ON' : 'OFF'} | samples: {remoteMotionSampleCount} | duration: {remoteMotionRecordingDurationText}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6 }}>
              <button
                onClick={() => {
                  if (isRemoteMotionRecording) {
                    setIsRemoteMotionRecording(false);
                    return;
                  }

                  if (remoteMotionSampleCount === 0) {
                    setRemoteMotionRecordStartedAtMs(Date.now());
                  } else if (remoteMotionRecordStartedAtMs === null) {
                    setRemoteMotionRecordStartedAtMs(Date.now());
                  }
                  setIsRemoteMotionRecording(true);
                }}
                style={{
                  border: '1px solid rgba(148, 163, 184, 0.45)',
                  borderRadius: 8,
                  background: isRemoteMotionRecording ? 'rgba(127, 29, 29, 0.9)' : 'rgba(22, 163, 74, 0.85)',
                  color: '#E2E8F0',
                  padding: '6px 8px',
                  cursor: 'pointer',
                  fontSize: 12
                }}
              >
                {isRemoteMotionRecording ? 'Stop' : 'Start'}
              </button>
              <button
                onClick={() => {
                  remoteMotionDebugLogRef.current = [];
                  (window as Window & { __remoteMotionDebugLog?: RemoteMotionDebugSample[] }).__remoteMotionDebugLog = [];
                  setRemoteMotionSampleCount(0);
                  setRemoteMotionRecordStartedAtMs(isRemoteMotionRecording ? Date.now() : null);
                }}
                style={{
                  border: '1px solid rgba(148, 163, 184, 0.45)',
                  borderRadius: 8,
                  background: 'rgba(30, 41, 59, 0.92)',
                  color: '#E2E8F0',
                  padding: '6px 8px',
                  cursor: 'pointer',
                  fontSize: 12
                }}
              >
                Clear
              </button>
              <button
                onClick={() => {
                  if (remoteMotionDebugLogRef.current.length === 0) {
                    return;
                  }

                  const payload = {
                    exportedAt: new Date().toISOString(),
                    sessionId,
                    sampleCount: remoteMotionDebugLogRef.current.length,
                    intervalMs: REMOTE_MOTION_RECORD_INTERVAL_MS,
                    tuners: {
                      interpolationSpeed: remoteInterpolationSpeed,
                      extrapolationLimitMs: remoteExtrapolationLimitMs,
                      extrapolationStaleMs: remoteExtrapolationStaleMs
                    },
                    samples: remoteMotionDebugLogRef.current
                  };

                  const safeSessionId = (sessionId ?? 'session').replace(/[^a-zA-Z0-9_-]+/g, '_');
                  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
                  const filename = `remote-motion-debug-${safeSessionId}-${timestamp}.json`;
                  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const anchor = document.createElement('a');
                  anchor.href = url;
                  anchor.download = filename;
                  anchor.click();
                  URL.revokeObjectURL(url);
                }}
                disabled={remoteMotionSampleCount === 0}
                style={{
                  border: '1px solid rgba(148, 163, 184, 0.45)',
                  borderRadius: 8,
                  background: remoteMotionSampleCount === 0 ? 'rgba(71, 85, 105, 0.55)' : 'rgba(37, 99, 235, 0.9)',
                  color: '#E2E8F0',
                  padding: '6px 8px',
                  cursor: remoteMotionSampleCount === 0 ? 'not-allowed' : 'pointer',
                  fontSize: 12
                }}
              >
                Download
              </button>
            </div>
          </div>

          <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
            <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
              Interpolation Speed ({remoteInterpolationSpeed.toFixed(1)})
              <input
                type="range"
                min={6}
                max={32}
                step={0.5}
                value={remoteInterpolationSpeed}
                onChange={(event) => {
                  const nextValue = clampNumber(Number(event.target.value), 6, 32, DEFAULT_REMOTE_INTERPOLATION_SPEED);
                  setRemoteInterpolationSpeed(nextValue);
                }}
              />
            </label>

            <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
              Extrapolation Limit ms ({remoteExtrapolationLimitMs.toFixed(0)})
              <input
                type="range"
                min={0}
                max={240}
                step={5}
                value={remoteExtrapolationLimitMs}
                onChange={(event) => {
                  const nextValue = clampNumber(Number(event.target.value), 0, 240, DEFAULT_REMOTE_EXTRAPOLATION_LIMIT_MS);
                  setRemoteExtrapolationLimitMs(nextValue);
                }}
              />
            </label>

            <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
              Extrapolation Stale Cutoff ms ({remoteExtrapolationStaleMs.toFixed(0)})
              <input
                type="range"
                min={80}
                max={500}
                step={10}
                value={remoteExtrapolationStaleMs}
                onChange={(event) => {
                  const nextValue = clampNumber(Number(event.target.value), 80, 500, DEFAULT_REMOTE_EXTRAPOLATION_STALE_MS);
                  setRemoteExtrapolationStaleMs(nextValue);
                }}
              />
            </label>

            <button
              onClick={() => {
                setRemoteInterpolationSpeed(DEFAULT_REMOTE_INTERPOLATION_SPEED);
                setRemoteExtrapolationLimitMs(DEFAULT_REMOTE_EXTRAPOLATION_LIMIT_MS);
                setRemoteExtrapolationStaleMs(DEFAULT_REMOTE_EXTRAPOLATION_STALE_MS);
              }}
              style={{
                border: '1px solid rgba(148, 163, 184, 0.45)',
                borderRadius: 8,
                background: 'rgba(30, 41, 59, 0.92)',
                color: '#E2E8F0',
                padding: '6px 10px',
                cursor: 'pointer',
                fontSize: 12
              }}
            >
              Reset Tuners
            </button>
          </div>

          <div style={{ marginTop: 10, borderTop: '1px solid rgba(148, 163, 184, 0.25)', paddingTop: 8, display: 'grid', gap: 6 }}>
            {remoteMotionDebugRows.length === 0 ? (
              <div style={{ fontSize: 12, color: '#94A3B8' }}>No remote players currently tracked.</div>
            ) : (
              remoteMotionDebugRows.map((row) => (
                <div
                  key={row.id}
                  style={{
                    border: '1px solid rgba(148, 163, 184, 0.3)',
                    borderRadius: 8,
                    padding: '6px 8px',
                    fontSize: 11,
                    background: 'rgba(30, 41, 59, 0.72)'
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: 3 }}>
                    {row.id}
                  </div>
                  <div>snapshot age: {row.snapshotAgeMs.toFixed(0)}ms | extrap: {row.extrapolationMs.toFixed(0)}ms</div>
                  <div>velocity: ({row.velocityX.toFixed(1)}, {row.velocityY.toFixed(1)}) px/s | speed: {row.velocityMagnitude.toFixed(1)}</div>
                  <div>display-target gap: {row.displayToTarget.toFixed(2)}px | moving: {row.isMoving ? 'yes' : 'no'}</div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
      {isSpectator && currentQuestion && (
        <DraggableResizableWindow
          title={`Question ${resolvedQuestionIndex + 1} / ${Math.max(totalQuestions, 1)}`}
          initialRect={spectatorQuestionWindowRect}
          minWidth={420}
          minHeight={190}
          zIndex={50}
          windowRef={headerRef}
          onRectChange={setSpectatorQuestionWindowRect}
          frameStyle={{
            backgroundColor: 'rgba(10, 25, 47, 0.82)',
            borderRadius: '18px',
            border: '1px solid rgba(255,255,255,0.15)',
            boxShadow: '0 12px 30px rgba(0,0,0,0.35)'
          }}
          contentStyle={{
            padding: 0,
            overflow: 'auto'
          }}
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'grid',
              gridTemplateRows: 'auto minmax(0, 1fr)',
              alignContent: 'start',
              gap: `${spectatorQuestionGap}px`,
              boxSizing: 'border-box',
              padding: `${spectatorQuestionPaddingY}px ${spectatorQuestionPaddingX}px`,
              overflowY: 'auto'
            }}
          >
            <div
              style={{
                fontSize: `${spectatorQuestionTitleFontSize}px`,
                fontWeight: 'bold',
                color: '#F8FAFC',
                textAlign: 'center',
                lineHeight: 1.35,
                overflowY: 'auto',
                maxHeight: `${spectatorQuestionTextMaxHeight}px`,
                scrollbarWidth: 'thin',
                overflowWrap: 'anywhere',
                textShadow: '0 2px 6px rgba(0,0,0,0.35)',
              }}
            >
              {currentQuestion.text}
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(auto-fit, minmax(${spectatorQuestionChoiceMinWidth}px, 1fr))`,
                gap: `${spectatorQuestionGap}px`,
                alignContent: 'start'
              }}
            >
              {currentQuestion.choices.map((choice, index) => {
                const color = questionHeaderColors[index % questionHeaderColors.length];
                return (
                  <div
                    key={index}
                    style={{
                      backgroundColor: color.bg,
                      border: `3px solid ${color.border}`,
                      color: color.text,
                      borderRadius: '14px',
                      padding: `${Math.round(clampNumber(14 * spectatorQuestionLayoutScale, 8, 18, 12))}px ${Math.round(clampNumber(16 * spectatorQuestionLayoutScale, 10, 20, 14))}px`,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: `${Math.round(clampNumber(10 * spectatorQuestionLayoutScale, 6, 12, 8))}px`,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                      textAlign: 'center',
                      minHeight: `${spectatorQuestionChoiceMinHeight}px`
                    }}
                  >
                    <div style={{ fontSize: `${spectatorQuestionChoiceBadgeFontSize}px`, fontWeight: 700, opacity: 0.9 }}>
                      {String.fromCharCode(65 + index)}
                    </div>
                    <div
                      style={{
                        fontSize: `${spectatorQuestionChoiceTextFontSize}px`,
                        lineHeight: 1.35,
                        overflowWrap: 'anywhere'
                      }}
                    >
                      {choice}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </DraggableResizableWindow>
      )}

      {!isSpectator && gameState === 'running' && currentQuestion && !showQuestionOverlay && (
        <ArenaQuestionPeekButton
          onShowQuestion={() => updateQuestionOverlayVisibility({ type: 'peek-button-click' })}
        />
      )}

      {/* Question Overlay - Shown by Tab or the top question peek button */}
      {!isSpectator && showQuestionOverlay && currentQuestion && (
        <div
          onPointerDown={(event) => {
            if (event.target === event.currentTarget) {
              event.preventDefault();
              updateQuestionOverlayVisibility({ type: 'question-backdrop-click' });
            }
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(2px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            pointerEvents: 'auto'
          }}
        >
          <div
            onPointerDown={(event) => {
              event.stopPropagation();
              updateQuestionOverlayVisibility({ type: 'question-card-click' });
            }}
            style={{
              maxWidth: '900px',
              margin: '0 auto',
              backgroundColor: 'rgba(10, 25, 47, 0.75)',
              borderRadius: '18px',
              border: '2px solid rgba(255,255,255,0.25)',
              padding: '24px 32px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
            }}
          >
            <div style={{
              fontSize: '1.8rem',
              fontWeight: 'bold',
              color: '#F8FAFC',
              textAlign: 'center',
              lineHeight: 1.4,
              marginBottom: '20px',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}>
              {currentQuestion.text}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '16px'
              }}
            >
              {currentQuestion.choices.map((choice, index) => {
                const color = questionHeaderColors[index % questionHeaderColors.length];
                return (
                  <div
                    key={index}
                    style={{
                      backgroundColor: color.bg,
                      border: `3px solid ${color.border}`,
                      color: color.text,
                      borderRadius: '14px',
                      padding: '1rem 1.2rem',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.6rem',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                      textAlign: 'center',
                      minHeight: '100px'
                    }}
                  >
                    <div style={{ fontSize: '1.4rem', fontWeight: 700, opacity: 0.95 }}>
                      {String.fromCharCode(65 + index)}
                    </div>
                    <div style={{ fontSize: '1.1rem', lineHeight: 1.3 }}>
                      {choice}
                    </div>
                  </div>
                );
              })}
            </div>
            {!isTouchDevice && (
              <div style={{
                marginTop: '16px',
                textAlign: 'center',
                color: 'rgba(255,255,255,0.7)',
                fontSize: '0.95rem',
                fontStyle: 'italic'
              }}>
                Hold Tab to view • Release to continue playing
              </div>
            )}
          </div>
        </div>
      )}

      <div style={worldContainerStyle}>

      {/* Running Timer */}
      <div style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        fontSize: '3rem',
        color: runningTimeRemaining <= 2 ? '#FF4444' : '#FFD700',
        fontWeight: 'bold',
        textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
        zIndex: 10,
        animation: runningTimeRemaining <= 2 ? 'pulse 0.3s infinite' : 'none'
      }}>
        {runningTimeRemaining}s
      </div>

      {/* Starting Area Box */}
      <div style={{
        position: 'absolute',
        left: `${STARTING_BOX_X}px`,
        top: `${STARTING_BOX_Y}px`,
        width: `${STARTING_BOX_WIDTH}px`,
        height: `${STARTING_BOX_HEIGHT}px`,
        border: '3px solid white',
        borderRadius: '8px',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        zIndex: 5
      }} />

      {/* Fluffy Creature Character - Only show for actual players, not spectators */}
      {!isSpectator && (
      <div
        className={`fluffy-creature-game ${isMoving && !currentPlayer?.isImmobilized ? 'running' : 'idle'}`}
        style={{
          position: 'absolute',
          left: `${playerPosition.x}px`,
          top: `${playerPosition.y}px`,
          width: `${CHARACTER_SIZE}px`,
          height: `${CHARACTER_SIZE}px`,
          zIndex: 20,
          transform: selectedChoice !== null ? 'scale(1.45)' : 'scale(1.15)', // 15% larger base size, 45% when selected
          transition: 'transform 0.3s ease',
          filter: currentPlayer?.isImmobilized
            ? 'drop-shadow(0 0 20px #ff4444) saturate(0.6)'
            : selectedChoice !== null
            ? 'drop-shadow(0 0 20px #FFD700)'
            : 'drop-shadow(0 0 8px rgba(255, 215, 0, 0.3)) drop-shadow(0 6px 12px rgba(0,0,0,0.4))', // Subtle golden glow
          '--primary-color': currentCharacter.primaryColor,
          '--secondary-color': currentCharacter.secondaryColor,
          '--accent-color': currentCharacter.accentColor,
          opacity: currentPlayer?.isImmobilized ? 0.8 : 1
        } as React.CSSProperties}
      >
        <div className={`creature-orientation-wrapper ${facingDirection}`}>
          <div className="creature-ears-game">
            <div className="creature-ear-game left"></div>
            <div className="creature-ear-game right"></div>
          </div>
          <div className="creature-body-game">
            <div className="creature-eyes-game">
              <div className="eye-game left"></div>
              <div className="eye-game right"></div>
            </div>
            {isMoving && <div className="dust-cloud-game"></div>}
          </div>
          <div className="creature-hands-game">
            <div className="creature-hand-game left"></div>
            <div className="creature-hand-game right"></div>
          </div>
          <div className="creature-feet-game">
            <div className="creature-foot-game left"></div>
            <div className="creature-foot-game right"></div>
          </div>

          {/* Net Overlay for Immobilized Player */}
          {currentPlayer?.isImmobilized && (
            <div style={{
              position: 'absolute',
              top: '-30px',
              left: '-30px',
              width: '140px',
              height: '140px',
              fontSize: '108px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 21,
              animation: 'netShake 0.5s ease-in-out infinite alternate',
              opacity: 0.95,
              filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.7)) drop-shadow(0 0 8px rgba(255,255,255,0.6))',
              textShadow: '0 0 4px #000, 0 0 8px #fff, 2px 2px 4px rgba(0,0,0,0.8)',
              color: '#ffffff'
            }}>
              🕸️
            </div>
          )}
        </div>

        {/* Player Label */}
        <div style={{
          position: 'absolute',
          top: '-45px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: currentPlayer?.isImmobilized ? '#ff4444' : '#FFD700',
          color: currentPlayer?.isImmobilized ? '#fff' : '#000',
          padding: '3px 10px', // Slightly larger padding
          borderRadius: '10px', // More rounded
          fontSize: '11px', // Slightly larger font
          fontWeight: 'bold',
          whiteSpace: 'nowrap',
          boxShadow: currentPlayer?.isImmobilized
            ? '0 0 8px rgba(255, 68, 68, 0.6)'
            : '0 0 8px rgba(255, 215, 0, 0.6)', // Subtle golden glow
          border: currentPlayer?.isImmobilized ? 'none' : '1px solid rgba(255, 215, 0, 0.8)', // Golden border
          zIndex: 1
        }}>
          {currentPlayer?.isImmobilized ? '🕸️ Trapped!' : '👑 You'}
        </div>

        {/* Player Arrow Pointer - shows for 1.5 seconds at game start */}
        {showPlayerArrow && (
          <div style={{
            position: 'absolute',
            top: '-60px', // Above the name tag
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: '24px',
            color: '#FFD700',
            textShadow: '0 0 10px rgba(255, 215, 0, 0.8)',
            animation: 'bounceArrow 0.6s ease-in-out infinite alternate',
            zIndex: 2
          }}>
            ⬇️
          </div>
        )}

      </div>
      )}

      {/* AI Characters - Multiple AI Players */}
      {aiPlayers.map((aiPlayer, index) => {
        // Define unique colors for each AI
        const aiColors = [
          { primary: '#4ECDC4', secondary: '#45B7B8', accent: '#6C7B7F', border: '#00FF88' }, // Ocean Friend
          { primary: '#9B59B6', secondary: '#8E44AD', accent: '#BB8FCE', border: '#9B59B6' }, // Purple Pal
          { primary: '#2ECC71', secondary: '#27AE60', accent: '#82E0AA', border: '#27AE60' }, // Forest Mate
          { primary: '#F39C12', secondary: '#E67E22', accent: '#F8C471', border: '#E67E22' }, // Sunny Sidekick
          { primary: '#2C3E50', secondary: '#34495E', accent: '#5D6D7E', border: '#34495E' }, // Midnight Companion
          { primary: '#FF6B8A', secondary: '#FF8FA3', accent: '#FFB3C1', border: '#FF6B8A' }, // Coral Buddy
          { primary: '#E74C3C', secondary: '#C0392B', accent: '#EC7063', border: '#C0392B' }, // Red variant
          { primary: '#3498DB', secondary: '#2980B9', accent: '#85C1E9', border: '#2980B9' }, // Blue variant
          { primary: '#1ABC9C', secondary: '#16A085', accent: '#7DCEA0', border: '#16A085' }, // Teal variant
          { primary: '#F1C40F', secondary: '#F39C12', accent: '#F8D7DA', border: '#F39C12' }, // Yellow variant
        ];

        const colorScheme = aiColors[index % aiColors.length];
        const aiPlayerData = players.find(p => p.id === aiPlayer.id);

        return (
          <div
            key={aiPlayer.id}
            className={`fluffy-creature-game ai-character ${aiPlayer.isMoving && !aiPlayerData?.isImmobilized ? 'running' : 'idle'}`}
            style={{
              position: 'absolute',
              left: `${aiPlayer.position.x}px`,
              top: `${aiPlayer.position.y}px`,
              width: `${CHARACTER_SIZE}px`,
              height: `${CHARACTER_SIZE}px`,
              zIndex: 18 - index, // Stagger AI players below player but above each other
              transform: aiPlayer.selectedChoice !== null ? 'scale(1.1)' : 'scale(0.85)', // Slightly smaller than player
              transition: 'transform 0.3s ease',
              filter: aiPlayerData?.isImmobilized
                ? 'drop-shadow(0 0 15px #ff4444) saturate(0.6)'
                : aiPlayer.selectedChoice !== null
                ? `drop-shadow(0 0 20px ${colorScheme.border})`
                : 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
              '--primary-color': colorScheme.primary,
              '--secondary-color': colorScheme.secondary,
              '--accent-color': colorScheme.accent,
              opacity: 0.9 // Slightly transparent to distinguish from player
            } as React.CSSProperties}
          >
            <div className={`creature-orientation-wrapper ${aiPlayer.facingDirection}`}>
              <div className="creature-ears-game">
                <div className="creature-ear-game left"></div>
                <div className="creature-ear-game right"></div>
              </div>
              <div className="creature-body-game">
                <div className="creature-eyes-game">
                  <div className="eye-game left"></div>
                  <div className="eye-game right"></div>
                </div>
                {aiPlayer.isMoving && <div className="dust-cloud-game"></div>}
              </div>
              <div className="creature-hands-game">
                <div className="creature-hand-game left"></div>
                <div className="creature-hand-game right"></div>
              </div>
              <div className="creature-feet-game">
                <div className="creature-foot-game left"></div>
                <div className="creature-foot-game right"></div>
              </div>

              {/* Net Overlay for Immobilized AI */}
              {aiPlayerData?.isImmobilized && (
                <div style={{
                  position: 'absolute',
                  top: '-26px',
                  left: '-26px',
                  width: '132px',
                  height: '132px',
                  fontSize: '96px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 19,
                  animation: 'netShake 0.5s ease-in-out infinite alternate',
                  opacity: 0.95,
                  filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.7)) drop-shadow(0 0 8px rgba(255,255,255,0.6))',
                  textShadow: '0 0 4px #000, 0 0 8px #fff, 2px 2px 4px rgba(0,0,0,0.8)',
                  color: '#ffffff'
                }}>
                  🕸️
                </div>
              )}
            </div>

            {/* AI Label */}
            <div style={{
              position: 'absolute',
              top: '-45px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: aiPlayerData?.isImmobilized ? '#ff4444' : colorScheme.border,
              color: '#fff',
              padding: '2px 8px',
              borderRadius: '8px',
              fontSize: '10px',
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
              boxShadow: aiPlayerData?.isImmobilized
                ? '0 0 6px rgba(255, 68, 68, 0.6)'
                : `0 0 6px ${colorScheme.border}40`,
              zIndex: 1
            }}>
              {aiPlayerData?.isImmobilized ? '🕸️ Trapped!' : `🤖 AI ${index + 1}`}
            </div>

          </div>
        );
      })}

      {/* Real Multiplayer Players - Other Human Players (or ALL players if spectating) */}
      {displayPlayers
        .filter(p =>
          !p.id.startsWith('ai-player-') && // Not AI
          (isSpectator || p.id !== resolvedCurrentPlayerId) && // Show all if spectating, otherwise exclude current player
          p.id !== 'current-player' // Not the default local player
        )
        .map((player, index) => {
          // Get character color scheme based on player's characterIndex
          const characterIndex = player.characterIndex ?? 0;
          const playerCharacter = CHARACTERS[characterIndex];
          const remoteRenderState = remotePlayerRenderStates.get(player.id);
          const renderedPosition = remoteRenderState?.position ?? player.position;
          const isPlayerMoving = remoteRenderState?.isMoving ?? false;
          const playerFacingDirection = remoteRenderState?.facingDirection ?? 'right';

          return (
            <div
              key={player.id}
              className={`fluffy-creature-game multiplayer-character ${isPlayerMoving && !player.isImmobilized ? 'running' : 'idle'}`}
              style={{
                position: 'absolute',
                left: `${renderedPosition.x}px`,
                top: `${renderedPosition.y}px`,
                width: `${CHARACTER_SIZE}px`,
                height: `${CHARACTER_SIZE}px`,
                zIndex: 17 - index, // Below AI players
                transform: player.lastAnswerIndex !== null && player.lastAnswerIndex !== undefined ? 'scale(1.1)' : 'scale(0.95)', // Slightly smaller than local player
                transition: 'transform 0.3s ease',
                filter: player.isImmobilized
                  ? 'drop-shadow(0 0 15px #ff4444) saturate(0.6)'
                  : player.lastAnswerIndex !== null && player.lastAnswerIndex !== undefined
                  ? `drop-shadow(0 0 20px ${playerCharacter.primaryColor})`
                  : 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
                '--primary-color': playerCharacter.primaryColor,
                '--secondary-color': playerCharacter.secondaryColor,
                '--accent-color': playerCharacter.accentColor,
                opacity: 0.95 // Slightly transparent to distinguish from local player
              } as React.CSSProperties}
            >
              <div className={`creature-orientation-wrapper ${playerFacingDirection}`}>
                <div className="creature-ears-game">
                  <div className="creature-ear-game left"></div>
                  <div className="creature-ear-game right"></div>
                </div>
                <div className="creature-body-game">
                  <div className="creature-eyes-game">
                    <div className="eye-game left"></div>
                    <div className="eye-game right"></div>
                  </div>
                  {isPlayerMoving && <div className="dust-cloud-game"></div>}
                </div>
                <div className="creature-hands-game">
                  <div className="creature-hand-game left"></div>
                  <div className="creature-hand-game right"></div>
                </div>
                <div className="creature-feet-game">
                  <div className="creature-foot-game left"></div>
                  <div className="creature-foot-game right"></div>
                </div>

                {/* Net Overlay for Immobilized Player */}
                {player.isImmobilized && (
                  <div style={{
                    position: 'absolute',
                    top: '-26px',
                    left: '-26px',
                    width: '132px',
                    height: '132px',
                    fontSize: '96px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 19,
                    animation: 'netShake 0.5s ease-in-out infinite alternate',
                    opacity: 0.95,
                    filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.7)) drop-shadow(0 0 8px rgba(255,255,255,0.6))',
                    textShadow: '0 0 4px #000, 0 0 8px #fff, 2px 2px 4px rgba(0,0,0,0.8)',
                    color: '#ffffff'
                  }}>
                    🕸️
                  </div>
                )}
              </div>

              {/* Player Name Label */}
              <div style={{
                position: 'absolute',
                top: '-45px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: player.isImmobilized ? '#ff4444' : playerCharacter.primaryColor,
                color: '#fff',
                padding: '2px 8px',
                borderRadius: '8px',
                fontSize: '10px',
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
                boxShadow: player.isImmobilized
                  ? '0 0 6px rgba(255, 68, 68, 0.6)'
                  : `0 0 6px ${playerCharacter.primaryColor}40`,
                zIndex: 1
              }}>
                {player.isImmobilized ? '🕸️ Trapped!' : `👤 ${player.name}`}
              </div>

            </div>
          );
        })}

      {/* Net Projectiles - Merged local + session nets */}
      {(() => {
        // Merge local nets with session nets (dedupe by ID)
        const localNets = activeNetProjectiles;
        const sessionNets = activeSession?.activeNetProjectiles || [];

        // Create a map to deduplicate by net ID (prefer local if exists)
        const netsMap = new Map();
        sessionNets.forEach(net => netsMap.set(net.id, net));
        localNets.forEach(net => netsMap.set(net.id, net)); // Local overrides session

        const allNets = Array.from(netsMap.values());
        return allNets.map(net => (
        <div
          key={net.id}
          style={{
            position: 'absolute',
            left: `${net.currentPosition.x - 60}px`, // Center the net (120px width / 2)
            top: `${net.currentPosition.y - 60}px`, // Center the net (120px height / 2)
            width: '120px',
            height: '120px',
            fontSize: '96px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 25,
            animation: net.isActive ? 'netSpin 0.4s linear infinite' : 'none',
            opacity: net.isActive ? 1 : 0.7,
            transform: `scale(${net.isActive ? 1 : 0.9})`,
            transition: 'opacity 0.3s ease, transform 0.3s ease',
            filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.6)) drop-shadow(0 0 8px rgba(255,255,255,0.8))',
            textShadow: '0 0 4px #000, 0 0 8px #fff, 2px 2px 4px rgba(0,0,0,0.8)',
            color: '#ffffff'
          }}
        >
          🕸️
        </div>
      ));
      })()}

      {/* Grenade Projectiles */}
      {(() => {
        // Firestore compatibility snapshots are hydrated into the game store.
        // Rendering them directly can resurrect stale in-flight grenade data
        // after the local/RTDB simulation has already exploded or removed it.
        const allGrenades = activeGrenadeProjectiles;

        return allGrenades.map(grenade => {
          // Determine rotation based on velocity for flying grenades
          const rotation = grenade.isActive
            ? `rotate(${((Date.now() - grenade.createdAt) / 5) % 360}deg)` // Spinning effect
            : 'rotate(0deg)';

          return (
            <React.Fragment key={grenade.id}>
              {/* Flying Grenade */}
              {grenade.isActive && (
                <div
                  style={{
                    position: 'absolute',
                    left: `${grenade.currentPosition.x - 30}px`,
                    top: `${grenade.currentPosition.y - 30}px`,
                    width: '60px',
                    height: '60px',
                    fontSize: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 25,
                    transform: rotation,
                    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.5))',
                    pointerEvents: 'none'
                  }}
                >
                  💣
                </div>
              )}

              {/* Explosion Effect */}
              {grenade.hasExploded && grenade.explosionPosition && (
                <div
                  style={{
                    position: 'absolute',
                    left: `${grenade.explosionPosition.x - 80}px`,
                    top: `${grenade.explosionPosition.y - 80}px`,
                    width: '160px',
                    height: '160px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 26,
                    pointerEvents: 'none',
                    animation: 'explosionExpand 0.5s ease-out forwards'
                  }}
                >
                  <div style={{
                    fontSize: '120px',
                    animation: 'explosionFade 0.6s ease-out forwards',
                    filter: 'drop-shadow(0 0 20px rgba(255,150,0,0.8))'
                  }}>
                    💥
                  </div>
                  {/* Additional explosion particles */}
                  <div style={{
                    position: 'absolute',
                    fontSize: '60px',
                    animation: 'explosionParticle1 0.5s ease-out forwards',
                    opacity: 0.8
                  }}>
                    ⭐
                  </div>
                  <div style={{
                    position: 'absolute',
                    fontSize: '50px',
                    animation: 'explosionParticle2 0.5s ease-out forwards',
                    opacity: 0.7
                  }}>
                    ✨
                  </div>
                  <div style={{
                    position: 'absolute',
                    fontSize: '55px',
                    animation: 'explosionParticle3 0.5s ease-out forwards',
                    opacity: 0.7
                  }}>
                    💫
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        });
      })()}

      {/* Grenade Charge Indicator - Above Player's Head */}
      {grenadeChargeStartTime !== null && (
        <div
          style={{
            position: 'absolute',
            left: `${playerPosition.x + 10}px`,
            top: `${playerPosition.y - 30}px`,
            width: '60px',
            height: '10px',
            backgroundColor: 'rgba(0,0,0,0.7)',
            borderRadius: '5px',
            overflow: 'hidden',
            border: '2px solid #fff',
            boxShadow: '0 0 10px rgba(0,0,0,0.5)',
            zIndex: 30
          }}
        >
          <div
            key={grenadeChargeStartTime}
            style={{
              height: '100%',
              width: '100%',
              background: 'linear-gradient(90deg, #FFD700 0%, #FF8C00 55%, #FF0000 100%)',
              transform: 'scaleX(0)',
              transformOrigin: 'left center',
              boxShadow: 'inset 0 0 8px rgba(255,255,255,0.5)',
              animation: `grenadeChargeFill ${GRENADE_MAX_CHARGE_MS}ms linear forwards, chargeGlow 0.5s ease-in-out infinite alternate`,
              willChange: 'transform'
            }}
          />
        </div>
      )}

      {/* Answer Areas */}
      {currentQuestion?.choices.map((_, index) => {
        const isSelected = selectedChoice === index;
        const isHovered = hoveredChoice === index;
        const isConsidering = consideringChoice === index;
        const selectionProgress = isConsidering ? (selectionTimer / 200) : 0;
        const { top: circleTop, left: circleLeft } = getAnswerCirclePosition(index, currentQuestion.choices.length);

        return (
          <div
            key={index}
            style={{
              position: 'absolute',
              left: `${circleLeft}px`,
              top: `${circleTop}px`,
              width: `${ANSWER_CIRCLE_SIZE}px`,
              height: `${ANSWER_CIRCLE_SIZE}px`,
              backgroundColor: answerColors[index % answerColors.length],
              borderRadius: '50%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '28px',
              fontWeight: 'bold',
              transform: isSelected
                ? 'scale(1.3)'
                : isConsidering
                ? 'scale(1.2)'
                : isHovered
                ? 'scale(1.1)'
                : 'scale(1)',
              transition: 'transform 0.2s ease, border 0.1s ease, box-shadow 0.1s ease',
              border: isSelected
                ? '6px solid #FFD700'
                : isConsidering
                ? '5px solid #00FF88'
                : isHovered
                ? '4px solid rgba(255,255,255,0.8)'
                : '3px solid rgba(255,255,255,0.3)',
              boxShadow: isSelected
                ? '0 0 25px rgba(255,215,0,0.9)'
                : isConsidering
                ? '0 0 20px rgba(0,255,136,0.7)'
                : isHovered
                ? '0 0 15px rgba(255,255,255,0.5)'
                : '0 4px 12px rgba(0,0,0,0.3)',
              animation: isHovered && !isConsidering && !isSelected ? 'pulse 1s infinite' : 'none',
              zIndex: 10
            }}
          >
            <div style={{ fontSize: '36px', marginBottom: 0 }}>
              {String.fromCharCode(65 + index)}
            </div>

            {/* Progress Ring for Considering State */}
            {isConsidering && !isSelected && (
              <div style={{
                position: 'absolute',
                top: '-8px',
                left: '-8px',
                right: '-8px',
                bottom: '-8px',
                borderRadius: '50%',
                background: `conic-gradient(#00FF88 ${selectionProgress * 360}deg, transparent 0deg)`,
                padding: '4px',
                zIndex: 1
              }}>
                <div style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  backgroundColor: 'transparent'
                }} />
              </div>
            )}

            {/* Instructional Text */}
            {isHovered && !isConsidering && !isSelected && (
              <div style={{
                position: 'absolute',
                bottom: '-35px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(255,255,255,0.9)',
                color: '#000',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: 'bold',
                whiteSpace: 'nowrap'
              }}>
                Move closer!
              </div>
            )}

            {isConsidering && !isSelected && (
              <div style={{
                position: 'absolute',
                bottom: '-35px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(0,255,136,0.9)',
                color: '#000',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: 'bold',
                whiteSpace: 'nowrap'
              }}>
                Hold position... {Math.ceil((200 - selectionTimer) / 100) / 10}s
              </div>
            )}
          </div>
        );
      })}

      {/* Instructions */}
      {!isSpectator && (
      <div style={{
        position: 'absolute',
        bottom: '30px',
        left: '50%',
        transform: 'translateX(-50%)',
        textAlign: 'center',
        color: 'white',
        fontSize: isTouchDevice ? '0.99rem' : '1.32rem',
        fontWeight: 'bold',
        textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
        backgroundColor: 'rgba(0,0,0,0.6)',
        padding: isTouchDevice ? '6px 14px' : '10px 20px',
        borderRadius: '20px',
        backdropFilter: 'blur(10px)',
        maxWidth: isTouchDevice ? '60%' : undefined,
      }}>
        <div style={{ marginBottom: '5px' }}>
          {selectedChoice !== null
            ? `✅ Answer ${String.fromCharCode(65 + selectedChoice)} locked in!`
            : consideringChoice !== null
            ? `⏱️ Hold position on ${String.fromCharCode(65 + consideringChoice)} for 0.2s to select`
            : hoveredChoice !== null
            ? `🎯 Move closer to answer ${String.fromCharCode(65 + hoveredChoice)} to select`
            : '🕹️ Move your creature to an answer circle and hold position!'
          }
        </div>
        {/* Turbo usage instruction with activation button — desktop only */}
        {!isTouchDevice
          && currentPlayer?.turboAvailable
          && !currentPlayer?.turboUsed
          && !currentPlayer?.isImmobilized && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '0.99rem',
            opacity: 0.9,
            color: '#FFD700'
          }}>
            <button
              onClick={() => {
                const { activateTurboItem: activateTurbo } = useGameStore.getState();
                if (resolvedCurrentPlayerId) {
                  activateTurbo(resolvedCurrentPlayerId);
                }
              }}
              style={{
                backgroundColor: '#FFD700',
                color: '#000',
                border: '2px solid #FFA500',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '0.99rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(255, 215, 0, 0.4)',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 215, 0, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 215, 0, 0.4)';
              }}
            >
              <span>⚡</span>
              <span>ACTIVATE TURBO</span>
            </button>
            <span style={{ fontSize: '0.94rem', opacity: 0.7 }}>
              (or press SPACE - 2s speed boost)
            </span>
          </div>
        )}
        {/* Net usage instruction with throw button — desktop only */}
        {!isTouchDevice && (currentPlayer?.netCharges || 0) > 0 && !currentPlayer?.isImmobilized && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '0.99rem',
            opacity: 0.9,
            color: '#00ff88'
          }}>
            <button
              onClick={() => {
                const { throwNet: throwNetItem } = useGameStore.getState();
                if (resolvedCurrentPlayerId) {
                  const throwDirection = facingDirectionRef.current === 'left' ? { x: -1, y: 0 } : { x: 1, y: 0 };
                  throwNetItem(resolvedCurrentPlayerId, throwDirection, playerPositionRef.current);
                }
              }}
              style={{
                backgroundColor: '#00ff88',
                color: '#000',
                border: '2px solid #00cc66',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '0.99rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0, 255, 136, 0.4)',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 255, 136, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 255, 136, 0.4)';
              }}
            >
              <span>🕸️</span>
              <span>THROW NET</span>
            </button>
            <span style={{ fontSize: '0.94rem', opacity: 0.7 }}>
              (or press SPACE - {currentPlayer?.netCharges} {currentPlayer?.netCharges === 1 ? 'charge' : 'charges'})
            </span>
          </div>
        )}
        {/* Shoes status display — desktop only */}
        {!isTouchDevice && !currentPlayer?.isImmobilized && (currentPlayer?.shoesEffectNextRound || hasActiveShoesModifier) && (
          <div style={{ fontSize: '0.99rem', opacity: 0.8, color: '#FF6B35' }}>
            👟 Speed Shoes active! Running {Math.round((SHOES_ITEM.effect.value - 1) * 100)}% faster this round
          </div>
        )}
        {/* Grenade usage instruction with charge button — desktop only */}
        {!isTouchDevice && currentPlayer && (currentPlayer.grenadeCharges || 0) > 0 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontSize: '0.99rem',
            opacity: 0.9,
            color: '#ff6b6b'
          }}>
            <button
              onMouseDown={() => {
                startGrenadeCharge();
              }}
              onMouseUp={() => {
                releaseGrenadeCharge();
              }}
              onMouseLeave={() => {
                cancelGrenadeCharge();
              }}
              style={{
                backgroundColor: grenadeChargeStartTime !== null ? '#ff4444' : '#ff6b6b',
                color: '#fff',
                border: '2px solid #cc5555',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '0.99rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: grenadeChargeStartTime !== null
                  ? '0 0 20px rgba(255, 68, 68, 0.8)'
                  : '0 2px 8px rgba(255, 107, 107, 0.4)',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transform: grenadeChargeStartTime !== null ? 'scale(1.1)' : 'scale(1)'
              }}
              onMouseEnter={(e) => {
                if (grenadeChargeStartTime === null) {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 107, 0.6)';
                }
              }}
            >
              <span>💣</span>
              <span>{grenadeChargeStartTime !== null ? 'CHARGING...' : 'THROW GRENADE'}</span>
            </button>
            <span style={{ fontSize: '0.94rem', opacity: 0.7 }}>
              (or hold SHIFT - {currentPlayer.grenadeCharges} {currentPlayer.grenadeCharges === 1 ? 'charge' : 'charges'})
            </span>
          </div>
        )}
        {/* AI selections are now shown with individual AI characters */}
      </div>
      )}



      {/* Mobile Touch Controls */}
      {isTouchDevice && !isSpectator && gameState === 'running' && (
        <>
          <VirtualJoystick onDirectionChange={handleJoystickChange} />
          <MobileActionButtons
            currentPlayer={currentPlayer}
            grenadeChargeStartTime={grenadeChargeStartTime}
            isImmobilized={!!currentPlayer?.isImmobilized}
            onPrimaryAction={handlePrimaryAction}
            onGrenadeStart={startGrenadeCharge}
            onGrenadeRelease={releaseGrenadeCharge}
            onGrenadeCancel={cancelGrenadeCharge}
          />
        </>
      )}

      {/* CSS Animations and Creature Styles */}
      <style>{`
        @keyframes pulse {
          0% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.1); }
          100% { opacity: 1; transform: scale(1); }
        }

        .fluffy-creature-game {
          transition: transform 0.1s ease;
        }

        .creature-orientation-wrapper {
          position: relative;
          width: 100%;
          height: 100%;
          transform-origin: center;
        }

        .creature-orientation-wrapper.left {
          transform: scaleX(-1);
        }

        .creature-orientation-wrapper.right {
          transform: scaleX(1);
        }

        .creature-body-game {
          position: relative;
          width: 100%;
          height: 100%;
          background: radial-gradient(circle at 30% 30%, var(--accent-color), var(--primary-color), var(--secondary-color));
          border-radius: 45% 55% 60% 40%;
          border: 4px solid var(--secondary-color);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: visible;
        }

        .creature-body-game::before {
          content: '';
          position: absolute;
          top: -3px;
          left: -3px;
          right: -3px;
          bottom: -3px;
          background: repeating-conic-gradient(
            from 0deg,
            var(--primary-color) 0deg 30deg,
            var(--secondary-color) 30deg 60deg,
            var(--accent-color) 60deg 90deg
          );
          border-radius: 45% 55% 60% 40%;
          z-index: -1;
          opacity: 0.4;
          filter: blur(1px);
        }

        .creature-body-game::after {
          content: '';
          position: absolute;
          width: 100%;
          height: 100%;
          background: repeating-radial-gradient(
            circle at 25% 25%,
            transparent 3px,
            var(--accent-color) 4px,
            transparent 6px,
            var(--primary-color) 8px
          );
          border-radius: 45% 55% 60% 40%;
          opacity: 0.5;
          mix-blend-mode: overlay;
        }

        .creature-eyes-game {
          display: flex;
          gap: 12px;
          z-index: 3;
          position: relative;
        }

        .eye-game {
          width: 25px;
          height: 25px;
          background: radial-gradient(circle at 30% 30%, #ffffff, #f0f0f0);
          border-radius: 50%;
          border: 3px solid #333;
          position: relative;
          animation: blinkGame 3s infinite;
        }

        .eye-game::after {
          content: '';
          position: absolute;
          width: 17px;
          height: 17px;
          background: radial-gradient(circle at 30% 30%, #1a1a1a, #000);
          border-radius: 50%;
          top: 1px;
          left: 2px;
        }

        .eye-game::before {
          content: '';
          position: absolute;
          width: 5px;
          height: 5px;
          background: rgba(255,255,255,0.9);
          border-radius: 50%;
          top: 3px;
          left: 4px;
          z-index: 1;
        }

        .creature-arms-game {
          position: absolute;
          top: 25px;
          width: 100%;
        }

        .arm-game {
          position: absolute;
          width: 10px;
          height: 30px;
          background: var(--primary-color);
          border: 3px solid var(--secondary-color);
          border-radius: 6px;
        }

        .left-arm {
          left: 8px;
          transform-origin: top;
        }

        .right-arm {
          right: 8px;
          transform-origin: top;
        }

        .creature-legs-game {
          position: absolute;
          bottom: -18px;
          width: 100%;
        }

        .leg-game {
          position: absolute;
          width: 10px;
          height: 25px;
          background: var(--primary-color);
          border: 3px solid var(--secondary-color);
          border-radius: 6px;
        }

        .left-leg {
          left: 18px;
        }

        .right-leg {
          right: 18px;
        }

        /* Running Animation */
        .fluffy-creature-game.running .left-arm {
          animation: armSwingLeft 0.4s infinite;
        }

        .fluffy-creature-game.running .right-arm {
          animation: armSwingRight 0.4s infinite;
        }

        .fluffy-creature-game.running .left-leg {
          animation: legRunLeft 0.4s infinite;
        }

        .fluffy-creature-game.running .right-leg {
          animation: legRunRight 0.4s infinite;
        }

        .fluffy-creature-game.running .creature-body-game {
          animation: bodyBounceGame 0.4s infinite;
        }

        /* Idle Animation */
        .fluffy-creature-game.idle .creature-body-game {
          animation: idleBreatheGame 3s infinite ease-in-out;
        }

        /* Dust Cloud Effect */
        .dust-cloud-game {
          position: absolute;
          bottom: -15px;
          left: 50%;
          transform: translateX(-50%);
          width: 25px;
          height: 10px;
          background: rgba(139, 69, 19, 0.4);
          border-radius: 50%;
          animation: dustPuffGame 0.4s infinite;
        }

        @keyframes bodyBounceGame {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-4px) rotate(1deg); }
        }

        @keyframes idleBreatheGame {
          0%, 100% { transform: scale(1) rotate(0deg); }
          25% { transform: scale(1.02) rotate(1deg); }
          50% { transform: scale(1.04) rotate(0deg); }
          75% { transform: scale(1.02) rotate(-1deg); }
        }

        @keyframes blinkGame {
          0%, 90%, 100% { transform: scaleY(1); }
          95% { transform: scaleY(0.1); }
        }

        @keyframes dustPuffGame {
          0% { opacity: 0.8; transform: translateX(-50%) scale(1); }
          100% { opacity: 0; transform: translateX(-50%) scale(1.8); }
        }

        @keyframes armSwingLeft {
          0%, 100% { transform: rotate(-20deg); }
          50% { transform: rotate(20deg); }
        }

        @keyframes armSwingRight {
          0%, 100% { transform: rotate(20deg); }
          50% { transform: rotate(-20deg); }
        }

        @keyframes legRunLeft {
          0%, 100% { transform: rotate(0deg); }
          50% { transform: rotate(-30deg); }
        }

        @keyframes legRunRight {
          0%, 100% { transform: rotate(-30deg); }
          50% { transform: rotate(0deg); }
        }

        @keyframes bodyBounce {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-3px); }
        }

        @keyframes idleBreathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }

        @keyframes dustPuff {
          0% { opacity: 0.7; transform: translateX(-50%) scale(1); }
          100% { opacity: 0; transform: translateX(-50%) scale(1.5); }
        }

        /* Rabbit Ears */
        .creature-ears-game {
          position: absolute;
          top: -20px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 12px;
          z-index: 1;
        }

        .creature-ear-game {
          width: 16px;
          height: 42px;
          background: linear-gradient(to bottom, var(--primary-color), var(--secondary-color));
          border: 3px solid var(--secondary-color);
          border-radius: 60% 40% 20% 80%;
          position: relative;
          animation: earWiggleGame 3s ease-in-out infinite;
          transform-origin: bottom center;
        }

        .creature-ear-game.left {
          transform: rotate(-15deg);
          animation-delay: 0.2s;
          transform-origin: bottom center;
        }

        .creature-ear-game.right {
          transform: rotate(15deg);
          animation-delay: 0.4s;
          animation-name: earWiggleRightGame;
          transform-origin: bottom center;
        }

        .creature-ear-game::before {
          content: '';
          position: absolute;
          width: 8px;
          height: 24px;
          background: var(--accent-color);
          border-radius: 50% 30% 20% 70%;
          top: 4px;
          left: 3px;
        }

        /* Small Hands */
        .creature-hands-game {
          position: absolute;
          top: 65%;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 45px;
          z-index: -1;
        }

        .creature-hand-game {
          width: 10px;
          height: 10px;
          background: var(--primary-color);
          border: 2px solid var(--secondary-color);
          border-radius: 50%;
          animation: handWaveGame 2.5s ease-in-out infinite;
        }

        .creature-hand-game.left {
          animation-delay: 0.1s;
        }

        .creature-hand-game.right {
          animation-delay: 0.3s;
        }

        /* Small Feet */
        .creature-feet-game {
          position: absolute;
          bottom: -10px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 25px;
          z-index: -1;
        }

        .creature-foot-game {
          width: 12px;
          height: 8px;
          background: var(--secondary-color);
          border: 2px solid var(--primary-color);
          border-radius: 60% 40% 50% 50%;
          animation: footTapGame 4s ease-in-out infinite;
        }

        .creature-foot-game.left {
          animation-delay: 0.5s;
        }

        .creature-foot-game.right {
          animation-delay: 1s;
        }

        @keyframes earWiggleGame {
          0%, 100% { transform: rotate(-15deg) scale(1); }
          50% { transform: rotate(-25deg) scale(1.02); }
        }

        @keyframes earWiggleRightGame {
          0%, 100% { transform: rotate(15deg) scale(1); }
          50% { transform: rotate(25deg) scale(1.02); }
        }

        @keyframes handWaveGame {
          0%, 100% { transform: translateY(0px) scale(1); }
          25% { transform: translateY(-3px) scale(1.1); }
          75% { transform: translateY(2px) scale(0.9); }
        }

        @keyframes footTapGame {
          0%, 90%, 100% { transform: translateY(0px); }
          95% { transform: translateY(-2px); }
        }

        /* Running Animation Enhancement */
        .fluffy-creature-game.running .creature-ear-game {
          animation: earWiggleFastGame 0.4s ease-in-out infinite;
        }

        .fluffy-creature-game.running .creature-hand-game {
          animation: handRunGame 0.4s ease-in-out infinite;
        }

        .fluffy-creature-game.running .creature-foot-game {
          animation: footRunGame 0.4s ease-in-out infinite;
        }

        @keyframes earWiggleFastGame {
          0%, 100% { transform: rotate(-20deg) scale(1); }
          50% { transform: rotate(-35deg) scale(1.05); }
        }

        @keyframes handRunGame {
          0%, 100% { transform: translateY(-2px) scale(1.2); }
          50% { transform: translateY(-5px) scale(1.3); }
        }

        @keyframes footRunGame {
          0%, 50%, 100% { transform: translateY(-3px); }
          25%, 75% { transform: translateY(-1px); }
        }

        /* Net Animation Effects */
        @keyframes netSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes netShake {
          0% { transform: translate(0px, 0px) scale(1); }
          25% { transform: translate(1px, -1px) scale(1.02); }
          50% { transform: translate(-1px, 1px) scale(0.98); }
          75% { transform: translate(1px, 1px) scale(1.01); }
          100% { transform: translate(-1px, -1px) scale(0.99); }
        }

        @keyframes bounceArrow {
          0% { transform: translateX(-50%) translateY(0px); }
          100% { transform: translateX(-50%) translateY(-8px); }
        }

        /* Grenade Animations */
        @keyframes explosionExpand {
          0% {
            transform: scale(0.5);
            opacity: 1;
          }
          50% {
            transform: scale(2.0);
            opacity: 0.9;
          }
          100% {
            transform: scale(2.5);
            opacity: 0;
          }
        }

        @keyframes explosionFade {
          0% {
            opacity: 1;
            transform: scale(0.8);
          }
          30% {
            opacity: 1;
            transform: scale(1.1);
          }
          100% {
            opacity: 0;
            transform: scale(1.3);
          }
        }

        @keyframes explosionParticle1 {
          0% {
            transform: translate(0, 0);
            opacity: 1;
          }
          100% {
            transform: translate(-40px, -40px);
            opacity: 0;
          }
        }

        @keyframes explosionParticle2 {
          0% {
            transform: translate(0, 0);
            opacity: 1;
          }
          100% {
            transform: translate(40px, -35px);
            opacity: 0;
          }
        }

        @keyframes explosionParticle3 {
          0% {
            transform: translate(0, 0);
            opacity: 1;
          }
          100% {
            transform: translate(0px, 45px);
            opacity: 0;
          }
        }

        @keyframes chargeGlow {
          0% {
            box-shadow: 0 0 5px rgba(255, 215, 0, 0.5);
          }
          100% {
            box-shadow: 0 0 15px rgba(255, 69, 0, 0.9);
          }
        }

        @keyframes grenadeChargeFill {
          0% {
            transform: scaleX(0);
          }
          100% {
            transform: scaleX(1);
          }
        }

        @keyframes playerTumble {
          0% { transform: rotate(0deg); }
          25% { transform: rotate(90deg) scale(1.1); }
          50% { transform: rotate(180deg) scale(1.05); }
          75% { transform: rotate(270deg) scale(1.1); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {/* Item Debug Display */}
      <ItemDebugDisplay position="bottom-left" isVisible={debugVisible} />

      </div>
    </div>
  );
};

export default GameArena;
